import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppStore, generateUUID } from '../stores/useAppStore'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiEndpoints } from '../config/env'
import { workOrderService } from '../services/workOrderService'
import { offlineWorkOrderService, type WorkOrderState } from '../services/offlineWorkOrderService'
import { offlineJsaService } from '../services/offlineJsaService'
import { messageDateService } from '../services/messageDateService'

// import { Textarea } from './ui/textarea'
import WorkOrderJSA from './WorkOrderJSA'
import InvoicePayment from './InvoicePayment'
import type { TechnicianWorkOrderDto, TechnicianPropertyContactDto, AlarmDto, WorkOrderAlarmPhotoDto, WorkOrderPhotoDto, TechnicianWorkOrderProps, TechnicianWorkOrderResponseDto } from '../types/workOrder'


const TechnicianWorkOrder = ({ workOrderId }: TechnicianWorkOrderProps) => {
  const { user, addNotification, selectedDate } = useAppStore()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [showJSA, setShowJSA] = useState(false)


  const [firstLoad, setFirstLoad] = useState(true)
  const [formData, setFormData] = useState<TechnicianWorkOrderDto>({} as TechnicianWorkOrderDto)
  const [existingAlarms, setExistingAlarms] = useState<AlarmDto[]>([])
  const [newAlarms, setNewAlarms] = useState<AlarmDto[]>([])
  
  // Photo management state
  const [showPhotosModal, setShowPhotosModal] = useState(false)
  const [currentAlarmPhotos, setCurrentAlarmPhotos] = useState<WorkOrderAlarmPhotoDto[]>([])
  const [selectedAlarmId, setSelectedAlarmId] = useState<number | string | null>(null)
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false)
  const [alarmPhotoCounts, setAlarmPhotoCounts] = useState<Record<string, number>>({})
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  // Work Order Photos state
  const [showWorkOrderPhotosModal, setShowWorkOrderPhotosModal] = useState(false)
  const [currentWorkOrderPhotos, setCurrentWorkOrderPhotos] = useState<WorkOrderPhotoDto[]>([])
  const [isLoadingWorkOrderPhotos, setIsLoadingWorkOrderPhotos] = useState(false)
  const [workOrderPhotoCount, setWorkOrderPhotoCount] = useState<number>(0)
  const workOrderFileInputRef = useRef<HTMLInputElement>(null)

  // Platform detection for camera capture on Android
  const isAndroid = /Android/i.test(navigator.userAgent)

  // Contacts state - make directly editable
  const [contacts, setContacts] = useState<TechnicianPropertyContactDto[]>([])

  // SMS modal state
  const [showSmsModal, setShowSmsModal] = useState(false)
  const [smsMessage, setSmsMessage] = useState('')
  const [selectedSmsContacts, setSelectedSmsContacts] = useState<number[]>([])
  const [smsType, setSmsType] = useState('')
  const [smsTypes, setSmsTypes] = useState<Array<{value: string, label: string, systemReference: string}>>([])
  const [isSendingSms, setIsSendingSms] = useState(false)
  // Set when the SMS modal was auto-opened from Save & Close, so closing it returns to the dashboard
  const navigateAfterSmsCloseRef = useRef(false)

  // Work order state tracking
  const [workOrderState, setWorkOrderState] = useState<WorkOrderState>({
    exists: false,
    hasPendingChanges: false,
    isSyncing: false,
    pendingCount: 0
  })

  // Alarm edit mode tracking - tracks which alarms are currently being edited
  const [alarmEditStates, setAlarmEditStates] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (newAlarms == null) {
      console.warn('newAlarms was set to null or undefined. Resetting to [].')
      setNewAlarms([])
    }
  }, [newAlarms])

  // Auto-save state
  const [isAutoSaving, setIsAutoSaving] = useState(false)
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // // Track online/offline status
  // useEffect(() => {
  //   const handleOnline = async () => {
      
  //     // Check if we have pending changes to sync
  //     const state = await offlineWorkOrderService.getWorkOrderState(workOrderId)
  //     if (state.hasPendingChanges) {
  //       addNotification({
  //         type: 'success',
  //         message: 'Connection restored. Syncing offline changes...',
  //         timestamp: Date.now()
  //       })
        
  //       // Trigger sync for this work order
  //       try {
  //         await offlineWorkOrderService.syncWorkOrder(workOrderId)
  //         const updatedState = await offlineWorkOrderService.getWorkOrderState(workOrderId)
  //         setWorkOrderState(updatedState)
          
  //         addNotification({
  //           type: 'success',
  //           message: 'Offline changes synced successfully!',
  //           timestamp: Date.now()
  //         })
  //       } catch (error) {
  //         console.error('Sync failed:', error)
  //         addNotification({
  //           type: 'error',
  //           message: 'Failed to sync offline changes. Will retry later.',
  //           timestamp: Date.now()
  //         })
  //       }
  //     } else {
  //       addNotification({
  //         type: 'success',
  //         message: 'Connection restored.',
  //         timestamp: Date.now()
  //       })
  //     }
  //   }

  //   const handleOffline = () => {
  //     addNotification({
  //       type: 'info',
  //       message: 'Working offline. Changes will sync when connection is restored.',
  //       timestamp: Date.now()
  //     })
  //   }

  //   window.addEventListener('online', handleOnline)
  //   window.addEventListener('offline', handleOffline)

  //   return () => {
  //     window.removeEventListener('online', handleOnline)
  //     window.removeEventListener('offline', handleOffline)
  //   }
  // }, [addNotification, workOrderId])

  // Maybe I can disable for now and have sync on a parent area, happen.
  // Periodic state refresh and auto-sync  
  useEffect(() => {
    const refreshState = async () => {
      if (workOrderId) {
        const state = await offlineWorkOrderService.getWorkOrderState(workOrderId)
        setWorkOrderState(state)
        
        // Auto-sync if online and has pending changes
        if (navigator.onLine && state.hasPendingChanges && !state.isSyncing) {
          try {
            await offlineWorkOrderService.syncWorkOrder(workOrderId)
            // Update state after sync
            const newState = await offlineWorkOrderService.getWorkOrderState(workOrderId)
            setWorkOrderState(newState)
          } catch (error) {
            console.error('Auto-sync failed:', error)
          }
        }
        
        // Auto-sync JSAs if online and has pending JSAs
        if (navigator.onLine && offlineJsaService.hasPendingJsas()) {
          try {
            await offlineJsaService.syncAllPendingJsas()
          } catch (error) {
            console.error('JSA auto-sync failed:', error)
          }
        }
      }
    }

    // Use shorter interval (10 seconds) if there are pending changes
    const syncInterval = workOrderState?.hasPendingChanges ? 10000 : 30000
    const interval = setInterval(refreshState, syncInterval)
    
    // Initial refresh
    refreshState()

    return () => clearInterval(interval)
  }, [workOrderId, workOrderState?.hasPendingChanges])

  // Fetch work order details using offline-first strategy
  const { data: workOrderResponse, isLoading, error } = useQuery({
    queryKey: ['workorder', workOrderId, 'technician'],
    queryFn: async (): Promise<TechnicianWorkOrderResponseDto> => {
      console.log('workorder setting workOrderResponse X', workOrderResponse)

      const result = await offlineWorkOrderService.loadWorkOrder(workOrderId)

      // Update state
      setWorkOrderState(result.state)
      
      return result.data;
    },
    enabled: !!workOrderId && !!user,
    staleTime: 0, // workOrderState?.hasPendingChanges ? 0 : 5 * 60 * 1000, // No cache when pending changes exist
    retry: false 
  })

  // Check for offline JSA state
  const [offlineJsaState, setOfflineJsaState] = useState<any>(null)
  
  // Check JSA status for work order with offline support
  const { data: jsaData, isLoading: isLoadingJSA } = useQuery({
    queryKey: ['workorder-jsa', workOrderId, offlineJsaState?.exists, offlineJsaState?.isSigned],
    queryFn: async (): Promise<{ jsaSignature?: string } | null> => {
      // If we're offline and have a signed JSA, return it immediately

      const jsaState = await offlineJsaService.getJsaState(user?.id, workOrderId)
      setOfflineJsaState(jsaState)

      if (!navigator.onLine && offlineJsaState?.exists && offlineJsaState?.isSigned) {
        return { jsaSignature: 'offline' }
      }
      
      // Try to fetch from server
      try {
        const response = await fetch(`${apiEndpoints.jsa}/workorder/${workOrderId}`)
        if (response.status === 404) {
          // No JSA on server, check offline
          if (offlineJsaState?.exists && offlineJsaState?.isSigned) {
            return { jsaSignature: 'offline' }
          }
          return null
        }
        if (!response.ok) {
          throw new Error('Failed to fetch JSA data')
        }
        return response.json()
      } catch (error) {
        // On any error (including network errors when offline), fall back to offline state
        if (offlineJsaState?.exists && offlineJsaState?.isSigned) {
          return { jsaSignature: 'offline' }
        }
        // Only throw if we don't have offline data
        if (navigator.onLine) {
          throw error
        }
        return null
      }
    },
    enabled: !!workOrderId && !!user,
    retry: false
  })
  
  useEffect(() => {
    const checkOfflineJsaState = async () => {
      if (workOrderId && user?.technicianId) {
        try {
          const state = await offlineJsaService.getJsaState(user.technicianId, workOrderId)
          setOfflineJsaState(state)
        } catch (error) {
          console.error('Error checking offline JSA state:', error)
        }
      }
    }
    
    checkOfflineJsaState()
  }, [workOrderId, user?.technicianId])

  // Refresh offline JSA state periodically to pick up new signatures
  useEffect(() => {
    if (!workOrderId || !user?.technicianId) return
    
    const refreshJsaState = async () => {
      try {
        const state = await offlineJsaService.getJsaState(user.technicianId, workOrderId)
        setOfflineJsaState(state)
      } catch (error) {
        console.error('Error refreshing offline JSA state:', error)
      }
    }
    
    // Check every 5 seconds for offline JSA changes
    const interval = setInterval(refreshJsaState, 5000)
    
    return () => clearInterval(interval)
  }, [workOrderId, user?.technicianId])

  const isJSASigned = !!jsaData?.jsaSignature || workOrderResponse?.workOrder?.jsaSigned || (offlineJsaState?.exists && offlineJsaState?.isSigned)

  // Auto-save function ref to avoid stale closures
  const autoSaveRef = useRef<(() => Promise<void>) | null>(null)
  
  // Debounced auto-save function
  const debouncedAutoSave = useCallback(() => {
    if (!isJSASigned) return

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }

    // Set new timeout for auto-save
    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (!isJSASigned) return // Double-check JSA status
      
      setIsAutoSaving(true)
      try {
        if (autoSaveRef.current) {
          await autoSaveRef.current()
        }
      } catch (error) {
        console.error('Auto-save failed:', error)
      } finally {
        setIsAutoSaving(false)
      }
    }, 1000) 
  }, [isJSASigned])

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [])

  // Wrapper functions to trigger auto-save
  const updateFormData = useCallback((newData: Partial<TechnicianWorkOrderDto>) => {
    setFormData(prev => ({ ...prev, ...newData }))
    //debouncedAutoSave()
  }, [debouncedAutoSave])

  const updateExistingAlarms = useCallback((alarmArr: AlarmDto[]) => {
    setExistingAlarms(alarmArr)
    //debouncedAutoSave()
  }, [debouncedAutoSave])

  const updateNewAlarms = useCallback((alarmArr: AlarmDto[]) => {
    setNewAlarms(alarmArr)
    //debouncedAutoSave()
  }, [debouncedAutoSave])

  // Helper function to sort alarms consistently
  const sortAlarms = (alarms: AlarmDto[]): AlarmDto[] => {
    return [...alarms].sort((a, b) => {
      // First try to sort by workOrderAlarmID if available
      if (a.workOrderAlarmID && b.workOrderAlarmID) {
        return a.workOrderAlarmID - b.workOrderAlarmID
      }
      // Fallback to creationDate if available
      if (a.creationDate && b.creationDate) {
        return new Date(a.creationDate).getTime() - new Date(b.creationDate).getTime()
      }
      // Keep original order if no sorting criteria available
      return 0
    })
  }

  // Helper function to sort compliance/legislation options in desired order
  const sortComplianceOptions = (options: any[]): any[] => {
    if (!options) return []

    // Support both compliance and legislation option ordering
    const desiredOrder = ['Compliant', 'Not Compliant', 'Upgraded', 'Not Upgraded', 'TBC']

    return [...options].sort((a, b) => {
      const aIndex = desiredOrder.indexOf(a.name)
      const bIndex = desiredOrder.indexOf(b.name)

      // If both options are in the desired order, sort by their position
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex
      }

      // If only one is in the desired order, prioritize it
      if (aIndex !== -1) return -1
      if (bIndex !== -1) return 1

      // If neither are in the desired order, keep original order
      return 0
    })
  }

  // Helper function to preserve local offlineIds when merging API data
  const preserveOfflineIds = (apiAlarms: AlarmDto[], localAlarms: AlarmDto[]): AlarmDto[] => {
    return apiAlarms.map(apiAlarm => {
      // If API alarm has no offlineId, try to find matching local alarm by workOrderAlarmID
      if (!apiAlarm.offlineId && apiAlarm.workOrderAlarmID) {
        const localAlarm = localAlarms.find(l => l.workOrderAlarmID === apiAlarm.workOrderAlarmID)
        if (localAlarm?.offlineId) {
          return { ...apiAlarm, offlineId: localAlarm.offlineId }
        }
      }
      return apiAlarm
    })
  }

  // Load data when response changes (but don't overwrite when pending changes exist)
  useEffect(() => {
    console.log('workorder setting workOrderResponse 1', workOrderResponse)
    if (workOrderResponse?.workOrder && (!workOrderState?.hasPendingChanges || firstLoad)) {
      console.log('workorder setting workOrderResponse 2', workOrderResponse)
      setFirstLoad(false)
      setFormData(workOrderResponse.workOrder)
      // Sort alarms to ensure consistent ordering, preserving local offlineIds if API returns null
      const apiExistingAlarms = workOrderResponse.existingAlarms || []
      const apiNewAlarms = workOrderResponse.newAlarms || []
      const sortedExistingAlarms = sortAlarms(preserveOfflineIds(apiExistingAlarms, existingAlarms))
      const sortedNewAlarms = sortAlarms(preserveOfflineIds(apiNewAlarms, newAlarms))
      setExistingAlarms(sortedExistingAlarms)
      setNewAlarms(sortedNewAlarms)
      setContacts(workOrderResponse.contacts || [])
      
      // Load photo counts for all alarms
      loadPhotoCountsForAlarms([...sortedExistingAlarms, ...sortedNewAlarms])

      // Load work order photo count
      fetchWorkOrderPhotoCount()

      // Set the message date to the work order's scheduled date or due date
      if (workOrderResponse.workOrder.woScheduledDate) {
        const scheduledDate = formatDate(workOrderResponse.workOrder.woScheduledDate)
        if (scheduledDate) {
          messageDateService.setMessageDate(scheduledDate)
        }
      } else if (workOrderResponse.workOrder.woDueDate) {
        const dueDate = formatDate(workOrderResponse.workOrder.woDueDate)
        if (dueDate) {
          messageDateService.setMessageDate(dueDate)
        }
      }
    }
  }, [workOrderResponse, workOrderState?.hasPendingChanges, firstLoad])

  // Safety Switch Business Logic (based on VB.NET reference code)
  useEffect(() => {
    if (!workOrderResponse?.lookupData) return

    // const lookupData = workOrderResponse.lookupData

    // Handle Safety Switch Test Required logic
    if (formData.safetySwitchReq === false) {
      // When "No" is selected, set the default notes only if empty
      if (!formData.safetySwitchNotes || formData.safetySwitchNotes.trim() === '') {
        setFormData(prev => ({
          ...prev,
          safetySwitchNotes: "The Safety Switch has been Sighted"
        }))
      }
    } else if (formData.safetySwitchReq === true) {
      // When "Yes" is selected, default to "Pass"
      setFormData(prev => ({
        ...prev,
        safetySwitchPass: true
      }))
    }

    // Handle Safety Switch Location logic
    // if (formData.safetySwitchLocationID_FK) {
    //   const unableToLocateLocation = lookupData.safetySwitchLocations?.find(l => 
    //     l.name.toLowerCase().includes('unable to locate') || 
    //     l.name.toLowerCase().includes('unabletolocate')
    //   )
      
    //   if (formData.safetySwitchLocationID_FK === unableToLocateLocation?.id) {
    //     // Clear notes when "Unable to locate" is selected
    //     setFormData(prev => ({
    //       ...prev,
    //       safetySwitchNotes: ""
    //     }))
    //   } else {
    //     // Set default notes for other selections (but only if safetySwitchReq is not false)
    //     if (formData.safetySwitchReq !== false) {
    //       setFormData(prev => ({
    //         ...prev,
    //         safetySwitchNotes: "The Safety Switch has been Sighted"
    //       }))
    //     }
    //   }
    // }
  }, [formData.safetySwitchReq, formData.safetySwitchLocationID_FK, workOrderResponse?.lookupData])

  // Package-based auto-selection logic
  useEffect(() => {
    if (!workOrderResponse?.workOrder) return

    const workOrder = workOrderResponse.workOrder

    // Auto-select Safety Switch based on package configuration (only if not manually set in database)
    if (workOrder.showSafetySwitch !== undefined && workOrder.safetySwitchReq === null && formData.safetySwitchReq === undefined) {
      setFormData(prev => ({
        ...prev,
        safetySwitchReq: workOrder.showSafetySwitch
      }))
    }

    // Auto-select Corded Blinds based on package configuration (only if not manually set in database)
    if (workOrder.showBlinds !== undefined && workOrder.cordedBlinkReq === null && formData.cordedBlinkReq === undefined) {
      setFormData(prev => ({
        ...prev,
        cordedBlinkReq: workOrder.showBlinds
      }))
    }
  }, [workOrderResponse?.workOrder, formData.safetySwitchReq, formData.cordedBlinkReq])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    // Prevent changes if JSA is not signed
    if (!isJSASigned) {
      return
    }
    
    const { name, value, type } = e.target
    let processedValue: string | number | boolean | null = value
    
    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked
    } else if (type === 'radio') {
      if (value === 'true') {
        processedValue = true
      } else if (value === 'false') {
        processedValue = false
      } else if (name.endsWith('ID_FK') || name.endsWith('_FK')) {
        // Convert to number for foreign key fields
        processedValue = Number(value)
      } else {
        processedValue = value
      }
    } else if (type === 'select-one') {
      // Handle select dropdowns with boolean values
      if (name === 'keyPickup') {
        if (value === 'true') {
          processedValue = true
        } else if (value === 'false') {
          processedValue = false
        }
      } else if (name.endsWith('ID_FK') || name.endsWith('_FK')) {
        // Convert to number for foreign key fields
        processedValue = Number(value)
      } else {
        processedValue = value
      }
    } else if (type === 'number') {
      processedValue = value === '' ? null : Number(value)
    } else if (['alarmsRe2022', 'noRooms', 'noLevels'].includes(name)) {
      // Handle numeric text fields (iPad-friendly - no spinner arrows)
      processedValue = value === '' ? null : Number(value)
    }

    // Auto-set inspection time when status changes to Completed (2)
    if (name === 'statusID_FK' && processedValue === 2) {
      const now = new Date()
      const hours = now.getHours().toString().padStart(2, '0')
      const minutes = now.getMinutes().toString().padStart(2, '0')
      const inspectionTimeValue = `${now.toISOString().split('T')[0]}T${hours}:${minutes}:00`
      updateFormData({ [name]: processedValue, inspectionTime: inspectionTimeValue })
      return
    }

    updateFormData({ [name]: processedValue })
  }

  // Save work order details


    const handleSaveWorkOrder = useCallback(
        async (
            overrideExistingAlarms?: AlarmDto[],
            overrideNewAlarms?: AlarmDto[]
        ) => {
            // Prevent saving if JSA is not signed
            if (!isJSASigned) {
                addNotification({
                    type: 'error',
                    message:
                        'Please complete and sign the Work Order JSA before making changes',
                    timestamp: Date.now(),
                });
                return;
            }

            // Capture the previous status to check if job was completed
            const previousStatusID =
                workOrderResponse?.workOrder?.statusID_FK;

            // Use override values if provided, otherwise use state
            const alarmsToSave = {
                existingAlarms:
                    overrideExistingAlarms ?? existingAlarms,
                newAlarms:
                    overrideNewAlarms ?? newAlarms,
            };

            try {
                // Format requestedTime as a DateTime string if it exists
                let formattedRequestedTime = null;

                if (formData.requestedTime) {
                    const timeStr = formData.requestedTime;

                    if (timeStr.includes('T')) {
                        // Already a full DateTime
                        formattedRequestedTime = timeStr;
                    } else if (
                        timeStr.match(/^\d{2}:\d{2}(:\d{2})?$/)
                    ) {
                        // Just a time string, combine with today's date
                        const today = new Date()
                            .toISOString()
                            .split('T')[0];

                        formattedRequestedTime = `${today}T${timeStr}:00`;
                    } else {
                        formattedRequestedTime = formData.requestedTime;
                    }
                }

                const updateData = {
                    WorkOrderID: workOrderId,
                    StatusID_FK: formData.statusID_FK,
                    InspectionTime: formData.inspectionTime,
                    RequestedTime: formattedRequestedTime,
                    OnArrivalID_FK: formData.onArrivalID_FK,
                    OnArrivalOther: formData.onArrivalOther,
                    AlarmStatusID_FK: formData.alarmStatusID_FK,
                    AlarmStatusOther: formData.alarmStatusOther,
                    QRCodeID_FK: formData.qrCodeID_FK,
                    QRCodeOther: formData.qrCodeOther,
                    KeyPickup: formData.keyPickup,
                    KeyNo: formData.keyNo,
                    SafetySwitchReq: formData.safetySwitchReq,
                    SafetySwitchPass: formData.safetySwitchPass,
                    UnableToTestReasonID_FK:
                        formData.unableToTestReasonID_FK,
                    UnableToTestReasonOther:
                        formData.unableToTestReasonOther,
                    SafetySwitchLocationID_FK:
                        formData.safetySwitchLocationID_FK,
                    SafetySwitchLocationOther:
                        formData.safetySwitchLocationOther,
                    SafetySwitchNotes: formData.safetySwitchNotes,
                    CordedBlinkReq: formData.cordedBlinkReq,
                    BlindsNo: formData.blindsNo,
                    CleatsNo: formData.cleatsNo,
                    TagsNo: formData.tagsNo,
                    NCBlinds: formData.ncBlinds,
                    CordedBlindNotes: formData.cordedBlindNotes,
                    SusAsbestos: formData.susAsbestos,
                    InspectionNotes: formData.inspectionNotes,
                    AlarmsRe2022: formData.alarmsRe2022,
                    NoRooms: formData.noRooms,
                    NoLevels: formData.noLevels,
                    StandardID_FK: formData.standardID_FK,
                    ComplianceID_FK: formData.complianceID_FK,
                    InvoiceAmount: formData.invoiceAmount,
                    InvoiceDate: formData.invoiceDate,
                    InvoiceStatus: formData.invoiceStatus,
                    InvoiceNo: formData.invoiceNo,
                    woOrderNotes: formData.woOrderNotes,
                    AnnualBillingFrom: formData.annualBillingFrom,
                    AnnualBillingTo: formData.annualBillingTo,
                    AnnualBillingMonth: formData.annualBillingMonth,
                    AnnualBillingDate: formData.annualBillingDate,
                    AnnualBillingInvoiceNo:
                        formData.annualBillingInvoiceNo,
                    AnnualBillingInvoiceAmount:
                        formData.annualBillingInvoiceAmount,
                    AnnualBillingInvoiceStatus:
                        formData.annualBillingInvoiceStatus,
                    Contacts: contacts,
                    NewAlarms: alarmsToSave.newAlarms,
                    ExistingAlarms: alarmsToSave.existingAlarms,
                };

                // Update the work order response with the current form values
                if (workOrderResponse?.workOrder) {
                    workOrderResponse.workOrder.inspectionTime =
                        formData.inspectionTime;
                    workOrderResponse.workOrder.requestedTime =
                        formData.requestedTime;
                    workOrderResponse.workOrder.onArrivalID_FK =
                        formData.onArrivalID_FK;
                    workOrderResponse.workOrder.onArrivalOther =
                        formData.onArrivalOther;
                    workOrderResponse.workOrder.alarmStatusID_FK =
                        formData.alarmStatusID_FK;
                    workOrderResponse.workOrder.alarmStatusOther =
                        formData.alarmStatusOther;
                    workOrderResponse.workOrder.qrCodeID_FK =
                        formData.qrCodeID_FK;
                    workOrderResponse.workOrder.qrCodeOther =
                        formData.qrCodeOther;
                    workOrderResponse.workOrder.keyPickup =
                        formData.keyPickup;
                    workOrderResponse.workOrder.keyNo =
                        formData.keyNo;
                    workOrderResponse.workOrder.safetySwitchReq =
                        formData.safetySwitchReq;
                    workOrderResponse.workOrder.safetySwitchPass =
                        formData.safetySwitchPass;
                    workOrderResponse.workOrder.unableToTestReasonID_FK =
                        formData.unableToTestReasonID_FK;
                    workOrderResponse.workOrder.unableToTestReasonOther =
                        formData.unableToTestReasonOther;
                    workOrderResponse.workOrder.safetySwitchLocationID_FK =
                        formData.safetySwitchLocationID_FK;
                    workOrderResponse.workOrder.safetySwitchLocationOther =
                        formData.safetySwitchLocationOther;
                    workOrderResponse.workOrder.safetySwitchNotes =
                        formData.safetySwitchNotes;
                    workOrderResponse.workOrder.cordedBlinkReq =
                        formData.cordedBlinkReq;
                    workOrderResponse.workOrder.blindsNo =
                        formData.blindsNo;
                    workOrderResponse.workOrder.cleatsNo =
                        formData.cleatsNo;
                    workOrderResponse.workOrder.tagsNo =
                        formData.tagsNo;
                    workOrderResponse.workOrder.ncBlinds =
                        formData.ncBlinds;
                    workOrderResponse.workOrder.cordedBlindNotes =
                        formData.cordedBlindNotes;
                    workOrderResponse.workOrder.susAsbestos =
                        formData.susAsbestos;
                    workOrderResponse.workOrder.inspectionNotes =
                        formData.inspectionNotes;
                    workOrderResponse.workOrder.alarmsRe2022 =
                        formData.alarmsRe2022;
                    workOrderResponse.workOrder.noRooms =
                        formData.noRooms;
                    workOrderResponse.workOrder.noLevels =
                        formData.noLevels;
                    workOrderResponse.workOrder.standardID_FK =
                        formData.standardID_FK;
                    workOrderResponse.workOrder.complianceID_FK =
                        formData.complianceID_FK;
                    workOrderResponse.workOrder.invoiceAmount =
                        formData.invoiceAmount;
                    workOrderResponse.workOrder.invoiceDate =
                        formData.invoiceDate;
                    workOrderResponse.workOrder.invoiceStatus =
                        formData.invoiceStatus;
                    workOrderResponse.workOrder.invoiceNo =
                        formData.invoiceNo;
                    workOrderResponse.workOrder.annualBillingFrom =
                        formData.annualBillingFrom;
                    workOrderResponse.workOrder.annualBillingTo =
                        formData.annualBillingTo;
                    workOrderResponse.workOrder.annualBillingMonth =
                        formData.annualBillingMonth;
                    workOrderResponse.workOrder.annualBillingDate =
                        formData.annualBillingDate;
                    workOrderResponse.workOrder.annualBillingInvoiceNo =
                        formData.annualBillingInvoiceNo;
                    workOrderResponse.workOrder.annualBillingInvoiceAmount =
                        formData.annualBillingInvoiceAmount;
                    workOrderResponse.workOrder.annualBillingInvoiceStatus =
                        formData.annualBillingInvoiceStatus;
                    workOrderResponse.workOrder.woOrderNotes =
                        formData.woOrderNotes;

                    workOrderResponse.newAlarms =
                        alarmsToSave.newAlarms;
                    workOrderResponse.existingAlarms =
                        alarmsToSave.existingAlarms;
                    workOrderResponse.contacts = contacts;
                }

                // Save the work order
                await offlineWorkOrderService.saveWorkOrder(
                    workOrderId,
                    updateData,
                    workOrderResponse!
                );

                // Update work order state
                const newState =
                    await offlineWorkOrderService.getWorkOrderState(
                        workOrderId
                    );

                setWorkOrderState(newState);

                // Invalidate React Query cache to force reload from IndexedDB
                queryClient.invalidateQueries({
                    queryKey: ['workorder', workOrderId, 'technician'],
                });

                // Show appropriate notification
                const message = newState.hasPendingChanges
                    ? 'Work order saved offline. Sync scheduled.'
                    : 'Work order saved successfully!';

                addNotification({
                    type: 'success',
                    message,
                    timestamp: Date.now(),
                });

                // If job was completed, update live ETA
                const isNowCompleted =
                    formData.statusID_FK === 2;

                const wasNotCompleted =
                    previousStatusID !== 2;

                console.log('Job completion check:', {
                    isNowCompleted,
                    wasNotCompleted,
                    previousStatusID,
                    currentStatusID: formData.statusID_FK,
                    technicianId: user?.technicianId,
                    selectedDate,
                });

                if (isNowCompleted) {
                    try {
                        console.log(
                            'Calling updateTechnicianETA after job completion',
                            {
                                technicianId: user.technicianId,
                                selectedDate,
                            }
                        );

                        await workOrderService.updateTechnicianETA(
                            workOrder.techUserID_FK,
                            selectedDate
                        );

                        addNotification({
                            type: 'success',
                            message:
                                'Job completed! ETAs updated for remaining stops.',
                            timestamp: Date.now(),
                        });
                    } catch (etaError) {
                        console.error('ETA update error:', etaError);

                        const etaMessage =
                            etaError instanceof Error
                                ? etaError.message
                                : 'Unknown error';

                        addNotification({
                            type: 'warning',
                            message: `Job saved but failed to update ETAs: ${etaMessage}`,
                            timestamp: Date.now(),
                        });
                    }
                }
            } catch (error) {
                console.error('Error saving work order:', error);

                addNotification({
                    type: 'error',
                    message: 'Failed to save work order',
                    timestamp: Date.now(),
                });
            }
        },
        [
            isJSASigned,
            workOrderId,
            formData,
            contacts,
            newAlarms,
            existingAlarms,
            workOrderResponse,
            queryClient,
            addNotification,
            offlineWorkOrderService,
            user,
            selectedDate,
        ]
    )

  // Update auto-save ref to point to the current handleSaveWorkOrder function
  useEffect(() => {
    autoSaveRef.current = handleSaveWorkOrder
  }, [handleSaveWorkOrder])

  // Main save handler
  const handleSave = async () => {
    // Prevent saving if JSA is not signed
    if (!isJSASigned) {
      addNotification({
        type: 'error',
        message: 'Please complete and sign the Work Order JSA before making changes',
        timestamp: Date.now()
      })
      return
    }
    
    await handleSaveWorkOrder()
    
    setTimeout(async () => {
      try {
        await offlineWorkOrderService.syncWorkOrder(workOrderId)
      } catch (error) {
        console.error('Auto-sync after save failed:', error)
      }
    }, 1000) 
  }

  // Payment success handler
  const handlePaymentSuccess = () => {
    addNotification({
      type: 'success',
      message: 'Payment completed successfully! Work order data has been refreshed.',
      timestamp: Date.now()
    })
    // Refresh work order data
    queryClient.invalidateQueries({ queryKey: ['workorder', workOrderId] })
  }

  // Client types that should be prompted for a Google review SMS on completion
  const isReviewSmsClientType = () => {
    const clientTypeName = workOrderResponse?.workOrder?.clientTypeName || ''
    const normalized = clientTypeName.toLowerCase().replace(/[^a-z]/g, '')
    return ['homeseller', 'homeowner', 'homebuyer'].includes(normalized)
  }

  // Save and Close handler
  const handleSaveAndClose = async () => {
    // First save the work order
    await handleSave()

    // For completed Home Seller / Homeowner / Home Buyer jobs, prompt the
    // technician to send the Google review SMS before returning to the dashboard
    if (formData.statusID_FK === 2 && isReviewSmsClientType()) {
      const opened = await handleOpenSmsModal('GoogleReview')
      if (opened) {
        navigateAfterSmsCloseRef.current = true
        return
      }
    }

    // Then navigate back to work orders list
    navigate('/work-orders')
    window.scrollTo(0, 0)
  }

  // Close Work Order handler (without saving)
  const handleCloseWorkOrder = () => {
    // Navigate back to work orders list without saving
    navigate('/work-orders')
    window.scrollTo(0, 0)
  }

  // SMS handlers
  const handleOpenSmsModal = async (preferredType?: string): Promise<boolean> => {
    try {
      // Load SMS types if not already loaded
      let types = smsTypes
      if (types.length === 0) {
        types = await workOrderService.getSmsTypes()
        setSmsTypes(types)
      }

      // Use the preferred type when provided (e.g. Google review prompt on Save & Close),
      // otherwise default to the first type if current smsType is empty or not in list
      let currentType = smsType
      if (preferredType && types.some(t => t.value === preferredType)) {
        currentType = preferredType
        setSmsType(currentType)
      } else if (!currentType || !types.some(t => t.value === currentType)) {
        currentType = types.length > 0 ? types[0].value : ''
        setSmsType(currentType)
      }

      // Get the SMS template from API for the selected type
      if (currentType) {
        const template = await workOrderService.getSmsTemplate(currentType)
        setSmsMessage(template.templateBody)
      } else {
        setSmsMessage('')
      }

      // Pre-select all contacts with valid phone numbers
      if (contacts && contacts.length > 0) {
        const contactsWithPhoneIndices = contacts
          .map((c, idx) => ({ phone: c.tenantPhone, index: idx }))
          .filter(c => c.phone && c.phone.trim() !== '')
          .map(c => c.index)
        setSelectedSmsContacts(contactsWithPhoneIndices)
      } else {
        setSelectedSmsContacts([])
      }

      setShowSmsModal(true)
      return true
    } catch (error) {
      console.error('Error loading SMS data:', error)
      addNotification({
        type: 'error',
        message: 'Failed to load SMS data',
        timestamp: Date.now()
      })
      return false
    }
  }

  const handleSmsTypeChange = async (newType: string) => {
    setSmsType(newType)
    try {
      // Load the template for the new type
      const template = await workOrderService.getSmsTemplate(newType)
      setSmsMessage(template.templateBody)
    } catch (error) {
      console.error('Error loading SMS template:', error)
      addNotification({
        type: 'error',
        message: 'Failed to load SMS template',
        timestamp: Date.now()
      })
    }
  }

  const handleSendSms = async () => {
    if (selectedSmsContacts.length === 0 || !smsMessage) {
      addNotification({
        type: 'error',
        message: 'Select at least one contact and enter a message',
        timestamp: Date.now()
      })
      return
    }

    setIsSendingSms(true)
    let successCount = 0
    let failCount = 0

    for (const idx of selectedSmsContacts) {
      const phoneNumber = contacts[idx]?.tenantPhone
      if (!phoneNumber) continue

      try {
        const response = await workOrderService.sendSms(workOrderId, phoneNumber, smsMessage, smsType)
        if (response.success) {
          successCount++
        } else {
          failCount++
        }
      } catch (error) {
        console.error('Error sending SMS to contact:', error)
        failCount++
      }
    }

    setIsSendingSms(false)

    if (failCount === 0) {
      addNotification({
        type: 'success',
        message: `SMS sent to ${successCount} contact(s)`,
        timestamp: Date.now()
      })
      setShowSmsModal(false)
      if (navigateAfterSmsCloseRef.current) {
        navigateAfterSmsCloseRef.current = false
        navigate('/work-orders')
        window.scrollTo(0, 0)
      }
    } else if (successCount > 0) {
      addNotification({
        type: 'warning',
        message: `SMS sent to ${successCount}, failed for ${failCount}`,
        timestamp: Date.now()
      })
    } else {
      addNotification({
        type: 'error',
        message: 'Failed to send SMS to all contacts',
        timestamp: Date.now()
      })
    }
  }

  const handleCloseSmsModal = () => {
    setShowSmsModal(false)
    setSmsMessage('')
    setSelectedSmsContacts([])
    // Don't reset smsType - keep the last selected type for next time
    if (navigateAfterSmsCloseRef.current) {
      navigateAfterSmsCloseRef.current = false
      navigate('/work-orders')
      window.scrollTo(0, 0)
    }
  }

  // Toggle contact selection for SMS
  const toggleSmsContactSelection = (idx: number) => {
    setSelectedSmsContacts(prev =>
      prev.includes(idx)
        ? prev.filter(i => i !== idx)
        : [...prev, idx]
    )
  }

  // Toggle edit mode for an alarm
  const handleEditAlarm = (alarmType: 'existing' | 'new', index: number) => {
    const alarmKey = `${alarmType}-${index}`
    setAlarmEditStates(prev => ({ ...prev, [alarmKey]: true }))
  }

  // Validate alarm has all required fields
  const validateAlarm = (alarm: AlarmDto): { isValid: boolean; missingFields: string[] } => {
    const missingFields: string[] = []

    if (!alarm.alarmTypeID_FK) missingFields.push('Type')
    if (!alarm.alarmLocationID_FK) missingFields.push('Location')
    if (!alarm.alarmBrandID_FK) missingFields.push('Brand')
    // Expiry fields are optional - removed from validation
    // if (!alarm.expiryM) missingFields.push('Expiry Month')
    // if (!alarm.expiryY) missingFields.push('Expiry Year')

    return {
      isValid: missingFields.length === 0,
      missingFields
    }
  }

  // Save individual alarm
  const handleSaveAlarm = async (alarmType: 'existing' | 'new', index: number) => {
    if (!isJSASigned) {
      addNotification({
        type: 'error',
        message: 'Please complete and sign the Work Order JSA before making changes',
        timestamp: Date.now()
      })
      return
    }

    // Get the alarm to validate and save
    const alarm = alarmType === 'existing' ? existingAlarms[index] : newAlarms[index]
    const alarmKey = `${alarmType}-${index}`

    // Validate the alarm
    const validation = validateAlarm(alarm)
    if (!validation.isValid) {
      addNotification({
        type: 'error',
        message: `Cannot save alarm. Missing required fields: ${validation.missingFields.join(', ')}`,
        timestamp: Date.now()
      })
      return
    }

    try {
      // Save the work order with current alarms
      await handleSaveWorkOrder()

      // Exit edit mode for this alarm on successful save
      setAlarmEditStates(prev => ({ ...prev, [alarmKey]: false }))

      addNotification({
        type: 'success',
        message: `${alarmType === 'existing' ? 'Existing' : 'New'} alarm ${index + 1} saved successfully!`,
        timestamp: Date.now()
      })
    } catch (error) {
      console.error('Error saving alarm:', error)
      addNotification({
        type: 'error',
        message: `Failed to save ${alarmType} alarm ${index + 1}`,
        timestamp: Date.now()
      })
    }
  }

  // Copy alarm (excluding location)
  const handleCopyAlarm = (alarmType: 'existing' | 'new', index: number) => {
    if (!isJSASigned) {
      addNotification({
        type: 'error',
        message: 'Please complete and sign the Work Order JSA before making changes',
        timestamp: Date.now()
      })
      return
    }

    const sourceAlarm = alarmType === 'existing' ? existingAlarms[index] : newAlarms[index]

    // Create a copy of the alarm excluding location fields
    const copiedAlarm: AlarmDto = {
      workOrderID_FK: workOrderId,
      alarmTypeID_FK: sourceAlarm.alarmTypeID_FK,
      alarmBrandID_FK: sourceAlarm.alarmBrandID_FK,
      alarmRFRID_FK: sourceAlarm.alarmRFRID_FK,
      alarmInstallationID_FK: sourceAlarm.alarmInstallationID_FK,
      sensorID_FK: sourceAlarm.sensorID_FK,
      sensor: "2", // Always set to PE
      dbID_FK: sourceAlarm.dbID_FK,
      db: sourceAlarm.db,
      battID_FK: sourceAlarm.battID_FK,
      batt: sourceAlarm.batt,
      expiryMonth: sourceAlarm.expiryMonth,
      expiryYear: sourceAlarm.expiryYear,
      expiryM: sourceAlarm.expiryM,
      expiryY: sourceAlarm.expiryY,
      // Exclude location fields - these will be undefined/empty
      alarmLocationID_FK: undefined,
      alarmLocation: undefined,
      alarmLocationOther: undefined,
      isExisting: alarmType === 'existing',
      offlineId: generateUUID()
    }

    if (alarmType === 'existing') {
      const updatedAlarms = sortAlarms([...existingAlarms, copiedAlarm])
      updateExistingAlarms(updatedAlarms)
      // Put the copied alarm in edit mode - it will be at the end after sorting
      const newIndex = updatedAlarms.length - 1
      setAlarmEditStates(prev => ({ ...prev, [`existing-${newIndex}`]: true }))
    } else {
      const updatedAlarms = sortAlarms([...newAlarms, copiedAlarm])
      updateNewAlarms(updatedAlarms)
      // Put the copied alarm in edit mode - it will be at the end after sorting
      const newIndex = updatedAlarms.length - 1
      setAlarmEditStates(prev => ({ ...prev, [`new-${newIndex}`]: true }))
    }

    addNotification({
      type: 'success',
      message: `${alarmType === 'existing' ? 'Existing' : 'New'} alarm ${index + 1} copied successfully! Location field cleared.`,
      timestamp: Date.now()
    })
  }

  // Import alarms from previous work order
  const handleImportAlarms = async () => {
    if (!isJSASigned || !formData.clientPropertyID_FK) {
      addNotification({
        type: 'error',
        message: 'JSA must be signed and property ID required to import alarms',
        timestamp: Date.now()
      })
      return
    }

    try {
      const importedAlarms = await workOrderService.importAlarms(
        workOrderId,
        Number(formData.clientPropertyID_FK)
      )
      
      if (importedAlarms && importedAlarms.length > 0) {
        // Convert imported alarms to AlarmDto format
        const newExistingAlarms = importedAlarms.map(alarm => ({
          workOrderID_FK: workOrderId,
          workOrderAlarmID: undefined,
          alarmTypeID_FK: alarm.alarmTypeID,
          alarmLocationID_FK: alarm.alarmLocationID,
          alarmBrandID_FK: alarm.alarmBrandID,
          alarmRFRID_FK: alarm.alarmRFRID,
          alarmNewInstallationID_FK: alarm.installationID,
          sensor: "2",
          db: alarm.db?.toString(),
          batt: alarm.batt,
          expiryM: alarm.expiryM,
          expiryY: alarm.expiryY,
          isExisting: true,
          offlineId: generateUUID()
        }))
        
        updateExistingAlarms(sortAlarms([...existingAlarms, ...newExistingAlarms]))
        debouncedAutoSave()
        
        addNotification({
          type: 'success',
          message: `Successfully imported ${importedAlarms.length} alarm(s) from previous work order`,
          timestamp: Date.now()
        })
      } else {
        addNotification({
          type: 'info',
          message: 'No alarms found to import from previous work orders',
          timestamp: Date.now()
        })
      }
    } catch (error) {
      console.error('Error importing alarms:', error)
      addNotification({
        type: 'error',
        message: 'Failed to import alarms. Please try again.',
        timestamp: Date.now()
      })
    }
  }


  // Handle contact field changes
  const handleContactChange = (index: number, field: keyof TechnicianPropertyContactDto, value: string | boolean) => {
    const updatedContacts = [...contacts]
    updatedContacts[index] = { ...updatedContacts[index], [field]: value }
    setContacts(updatedContacts)
  }

  // Photo handling functions
  const handleViewImages = async (alarmId: number | string) => {
    if (!alarmId) {
      addNotification({
        type: 'error',
        message: 'Invalid alarm ID',
        timestamp: Date.now()
      })
      return
    }

    setIsLoadingPhotos(true)
    setSelectedAlarmId(alarmId)

    //if not a number tell user,  must save first
    if (typeof alarmId !== 'number') {
      setShowPhotosModal(true)
      addNotification({
        type: 'error',
        message: 'Please save the work order first to view photos',
        timestamp: Date.now()
      })
      return
    }
    
    try {
      const response = await fetch(apiEndpoints.alarms.getPhotos(Number(alarmId)))
      if (!response.ok) {
        throw new Error('Failed to fetch photos')
      }
      
      const photos: WorkOrderAlarmPhotoDto[] = await response.json()
      setCurrentAlarmPhotos(photos)
      setShowPhotosModal(true)
    } catch (error) {
      console.error('Error fetching photos:', error)
      addNotification({
        type: 'error',
        message: 'Failed to load photos',
        timestamp: Date.now()
      })
    } finally {
      setIsLoadingPhotos(false)
    }
  }

  // Function to fetch photo count for an alarm
  const fetchPhotoCount = async (alarmId: number | string): Promise<number> => {
    if (!alarmId || typeof alarmId !== 'number') {
      return 0
    }
    
    try {
      const response = await fetch(apiEndpoints.alarms.getPhotos(Number(alarmId)))
      if (!response.ok) {
        return 0
      }
      
      const photos: WorkOrderAlarmPhotoDto[] = await response.json()
      return photos.length
    } catch (error) {
      console.error('Error fetching photo count:', error)
      return 0
    }
  }

  // Function to load photo counts for all alarms
  const loadPhotoCountsForAlarms = async (alarms: AlarmDto[]) => {
    const counts: Record<string, number> = {}

    for (const alarm of alarms) {
      const alarmKey = (alarm.workOrderAlarmID || alarm.offlineId || '').toString()
      if (alarmKey && alarm.workOrderAlarmID) {
        counts[alarmKey] = await fetchPhotoCount(alarm.workOrderAlarmID)
      } else {
        counts[alarmKey] = 0
      }
    }

    setAlarmPhotoCounts(prev => ({ ...prev, ...counts }))
  }

  // Function to fetch work order photo count
  const fetchWorkOrderPhotoCount = async () => {
    if (!workOrderId) return
    try {
      const response = await fetch(apiEndpoints.workOrders.getPhotos(workOrderId))
      if (response.ok) {
        const photos = await response.json()
        setWorkOrderPhotoCount(photos.length)
      }
    } catch (error) {
      console.error('Error fetching work order photo count:', error)
    }
  }

  const handleUploadImages = (alarmId: number | string) => {
    if (!alarmId) {
      addNotification({
        type: 'error',
        message: 'Invalid alarm ID',
        timestamp: Date.now()
      })
      return
    }

    // Trigger auto-save before uploading photos
    // debouncedAutoSave()

    setSelectedAlarmId(alarmId)
    fileInputRef.current?.click()
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0 || !selectedAlarmId) return

    // Convert FileList to Array for easier processing
    const filesArray = Array.from(files)

    // Validate all files before uploading
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
    const maxSize = 10 * 1024 * 1024 // 10MB

    const invalidFiles = filesArray.filter(file => !allowedTypes.includes(file.type))
    if (invalidFiles.length > 0) {
      addNotification({
        type: 'error',
        message: `${invalidFiles.length} file(s) skipped: Only image files (JPEG, PNG, GIF) are allowed`,
        timestamp: Date.now()
      })
    }

    const oversizedFiles = filesArray.filter(file => file.size > maxSize)
    if (oversizedFiles.length > 0) {
      addNotification({
        type: 'error',
        message: `${oversizedFiles.length} file(s) skipped: File size must be less than 10MB`,
        timestamp: Date.now()
      })
    }

    // Filter to valid files only
    const validFiles = filesArray.filter(file =>
      allowedTypes.includes(file.type) && file.size <= maxSize
    )

    if (validFiles.length === 0) {
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      setSelectedAlarmId(null)
      return
    }

    // Show upload progress notification
    if (validFiles.length > 1) {
      addNotification({
        type: 'info',
        message: `Uploading ${validFiles.length} photos...`,
        timestamp: Date.now()
      })
    }

    let successCount = 0
    let failCount = 0

    // Upload each file sequentially
    for (const file of validFiles) {
      try {
        await offlineWorkOrderService.uploadPhoto(workOrderId, file, selectedAlarmId)
        successCount++
      } catch (error) {
        console.error('Error uploading photo:', error)
        failCount++
      }
    }

    // Show completion notification
    if (successCount > 0) {
      addNotification({
        type: 'success',
        message: `Successfully uploaded ${successCount} photo${successCount > 1 ? 's' : ''}${failCount > 0 ? ` (${failCount} failed)` : ''}`,
        timestamp: Date.now()
      })

      // Refresh photos modal if open
      if (showPhotosModal && selectedAlarmId) {
        await handleViewImages(selectedAlarmId)
      }

      // Update photo count for the alarm
      if (selectedAlarmId && typeof selectedAlarmId === 'number') {
        const newCount = await fetchPhotoCount(selectedAlarmId)
        setAlarmPhotoCounts(prev => ({
          ...prev,
          [selectedAlarmId.toString()]: newCount
        }))
      }
    } else if (failCount > 0) {
      addNotification({
        type: 'error',
        message: `Failed to upload ${failCount} photo${failCount > 1 ? 's' : ''}`,
        timestamp: Date.now()
      })
    }

    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setSelectedAlarmId(null)
  }

  // Work Order Photo handling functions
  const handleViewWorkOrderPhotos = async () => {
    // Trigger auto-save before viewing work order photos
    //debouncedAutoSave()
    
    setIsLoadingWorkOrderPhotos(true)
    
    try {
      const response = await fetch(apiEndpoints.workOrders.getPhotos(workOrderId))
      if (!response.ok) {
        throw new Error('Failed to fetch work order photos')
      }
      
      const photos: WorkOrderPhotoDto[] = await response.json()
      setCurrentWorkOrderPhotos(photos)
      setShowWorkOrderPhotosModal(true)
    } catch (error) {
      console.error('Error fetching work order photos:', error)
      addNotification({
        type: 'error',
        message: 'Failed to load work order photos',
        timestamp: Date.now()
      })
    } finally {
      setIsLoadingWorkOrderPhotos(false)
    }
  }

  const handleUploadWorkOrderPhoto = () => {
    // Trigger auto-save before uploading work order photos
    //debouncedAutoSave()
    
    workOrderFileInputRef.current?.click()
  }

  const handleWorkOrderFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    // Convert FileList to Array for easier processing
    const filesArray = Array.from(files)

    // Validate all files before uploading
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
    const maxSize = 10 * 1024 * 1024 // 10MB

    const invalidFiles = filesArray.filter(file => !allowedTypes.includes(file.type))
    if (invalidFiles.length > 0) {
      addNotification({
        type: 'error',
        message: `${invalidFiles.length} file(s) skipped: Only image files (JPEG, PNG, GIF) are allowed`,
        timestamp: Date.now()
      })
    }

    const oversizedFiles = filesArray.filter(file => file.size > maxSize)
    if (oversizedFiles.length > 0) {
      addNotification({
        type: 'error',
        message: `${oversizedFiles.length} file(s) skipped: File size must be less than 10MB`,
        timestamp: Date.now()
      })
    }

    // Filter to valid files only
    const validFiles = filesArray.filter(file =>
      allowedTypes.includes(file.type) && file.size <= maxSize
    )

    if (validFiles.length === 0) {
      // Clear the file input
      if (workOrderFileInputRef.current) {
        workOrderFileInputRef.current.value = ''
      }
      return
    }

    // Show upload progress notification
    if (validFiles.length > 1) {
      addNotification({
        type: 'info',
        message: `Uploading ${validFiles.length} photos...`,
        timestamp: Date.now()
      })
    }

    let successCount = 0
    let failCount = 0

    // Upload each file sequentially
    for (const file of validFiles) {
      try {
        await offlineWorkOrderService.uploadPhoto(workOrderId, file)
        successCount++
      } catch (error) {
        console.error('Error uploading work order photo:', error)
        failCount++
      }
    }

    // Show completion notification
    if (successCount > 0) {
      addNotification({
        type: 'success',
        message: `Successfully uploaded ${successCount} photo${successCount > 1 ? 's' : ''}${failCount > 0 ? ` (${failCount} failed)` : ''}`,
        timestamp: Date.now()
      })

      // Update work order photo count
      setWorkOrderPhotoCount(prev => prev + successCount)

      // Refresh photos modal if open
      if (showWorkOrderPhotosModal) {
        await handleViewWorkOrderPhotos()
      }
    } else if (failCount > 0) {
      addNotification({
        type: 'error',
        message: `Failed to upload ${failCount} photo${failCount > 1 ? 's' : ''}`,
        timestamp: Date.now()
      })
    }

    // Clear the file input
    if (workOrderFileInputRef.current) {
      workOrderFileInputRef.current.value = ''
    }
  }

  // Delete alarm photo handler
  const handleDeleteAlarmPhoto = async (photoId: number) => {
    if (!confirm('Are you sure you want to delete this photo?')) {
      return
    }

    try {
      const response = await fetch(apiEndpoints.alarms.deletePhoto(photoId), {
        method: 'DELETE',
      })

      if (!response.ok) {
        throw new Error('Failed to delete photo')
      }

      addNotification({
        type: 'success',
        message: 'Photo deleted successfully',
        timestamp: Date.now()
      })

      // Remove photo from current list
      setCurrentAlarmPhotos(prev => prev.filter(p => p.workOrderAlarmPhotoID !== photoId))

      // Update photo count
      if (selectedAlarmId && typeof selectedAlarmId === 'number') {
        const newCount = await fetchPhotoCount(selectedAlarmId)
        setAlarmPhotoCounts(prev => ({
          ...prev,
          [selectedAlarmId.toString()]: newCount
        }))
      }
    } catch (error) {
      console.error('Error deleting alarm photo:', error)
      addNotification({
        type: 'error',
        message: 'Failed to delete photo',
        timestamp: Date.now()
      })
    }
  }

  // Delete work order photo handler
  const handleDeleteWorkOrderPhoto = async (photoId: number) => {
    if (!confirm('Are you sure you want to delete this photo?')) {
      return
    }

    try {
      const response = await fetch(apiEndpoints.files.deleteWorkOrderPhoto(photoId), {
        method: 'DELETE',
      })

      if (!response.ok) {
        throw new Error('Failed to delete photo')
      }

      addNotification({
        type: 'success',
        message: 'Photo deleted successfully',
        timestamp: Date.now()
      })

      // Remove photo from current list
      setCurrentWorkOrderPhotos(prev => prev.filter(p => p.workOrderPhotoID !== photoId))

      // Update work order photo count
      setWorkOrderPhotoCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Error deleting work order photo:', error)
      addNotification({
        type: 'error',
        message: 'Failed to delete photo',
        timestamp: Date.now()
      })
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return ''
    // For API dates like "2025-07-16 00:00:00.000", extract date part directly
    // to avoid timezone conversion issues
    if (dateString.includes(' ')) {
      return dateString.split(' ')[0]
    }
    // Handle ISO format dates
    if (dateString.includes('T')) {
      return dateString.split('T')[0]
    }
    return dateString
  }

  const formatTime = (timeString?: string) => {
    if (!timeString) return ''
    if (timeString.includes('T')) {
      return new Date(timeString).toTimeString().slice(0, 5)
    }
    return timeString
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <p className="ml-3">Loading work order details...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <h4 className="font-bold">Error loading work order</h4>
        <p>Please check your connection and try again.</p>
      </div>
    )
  }

  const workOrder = workOrderResponse?.workOrder
  const lookupData = workOrderResponse?.lookupData

  if (!workOrder) {
    return (
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
        <h4 className="font-bold">Work order not found</h4>
        <p>The requested work order could not be found.</p>
      </div>
    )
  }

  // Show JSA component if requested
  if (showJSA) {
    return (
      <WorkOrderJSA 
        workOrderId={workOrderId}
        onBack={() => setShowJSA(false)}
      />
    )
  }

  return (
    <div className="w-full p-3 bg-gray-50 min-h-screen">
      {/* Work Order Status Indicators */}
      {workOrderState.isSyncing && (
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-3">
          <div className="flex items-center">
            <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="font-medium">Syncing...</span>
            <span className="ml-2">Uploading changes to server</span>
          </div>
        </div>
      )}

      {workOrderState.hasPendingChanges && !workOrderState.isSyncing && (
        <div className="bg-orange-100 border border-orange-400 text-orange-700 px-4 py-3 rounded mb-3">
          <div className="flex items-center">
            <span className="font-medium">⏳ Pending Upload</span>
            <span className="ml-2">- {workOrderState.pendingCount} change(s) waiting to sync</span>
          </div>
        </div>
      )}

      {/* {workOrderState.isOffline && !workOrderState.hasPendingChanges && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-3">
          <div className="flex items-center">
            <span className="font-medium">📱 Offline Mode</span>
            <span className="ml-2">- Using cached data. Changes will sync when online.</span>
          </div>
        </div>
      )} */}

      {/* {!workOrderState.hasPendingChanges && !workOrderState.isSyncing && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-3">
          <div className="flex items-center">
            <span className="font-medium">✅ Synced</span>
            <span className="ml-2">- All changes saved to server</span>
            {workOrderState.lastSyncTime && (
              <span className="ml-2 text-sm">
                (Last sync: {new Date(workOrderState.lastSyncTime).toLocaleTimeString()})
              </span>
            )}
          </div>
        </div>
      )} */}

      {/* JSA Required Notification */}
      {!isJSASigned && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-3">
          <div className="flex items-center">
            <span className="font-medium">Work Order is Read-Only</span>
            <span className="ml-2">- Please complete and sign the Work Order JSA to enable editing</span>
          </div>
        </div>
      )}
      
      {/* Offline JSA Signed Notification */}
      {isJSASigned && offlineJsaState?.exists && offlineJsaState?.isSigned && !jsaData?.jsaSignature && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-3">
          <div className="flex items-center">
            <span className="font-medium">✅ JSA Signed Offline</span>
            <span className="ml-2">- Work Order is now editable. JSA will sync when connection is restored.</span>
            {offlineJsaState?.pendingSync && (
              <span className="ml-2 text-orange-600">(Pending sync)</span>
            )}
          </div>
        </div>
      )}

      {/* Auto-save indicator */}
      {isAutoSaving && (
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-3">
          <div className="flex items-center">
            <span className="font-medium">💾 Auto-saving...</span>
            <span className="ml-2">- Changes are being saved automatically</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap -mx-2 mb-3">
        <div className="w-full px-2">
          <h4 className="text-blue-600 mb-0 text-xl font-medium">
                      Work Order Details: {workOrder.clientName} -{' '}
                      {workOrder.buildingName
                          ? `${workOrder.buildingName}, `
                          : ''}
                      {workOrder.propertyAddress}
          </h4>
          {workOrder.packageDesc && (
            <div className="mt-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                Package: {workOrder.packageDesc}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* JSA and Property History Links */}
      <div className="flex flex-wrap -mx-2 mb-3">
        <div className="w-full md:w-1/2 px-2 mb-3 md:mb-0">
          <button 
            onClick={() => setShowJSA(true)}
            className={`font-bold py-2 px-4 rounded w-full block text-center ${
              isJSASigned 
                ? 'bg-gray-500 hover:bg-gray-600 text-white'
                : 'bg-red-500 hover:bg-red-700 text-white'
            }`}
          >
            Work Order JSA
          </button>
        </div>
        <div className="w-full md:w-1/2 px-2">
          <a 
            href={`/property-history/${workOrder.clientPropertyID_FK}?workOrderId=${workOrderId}`}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-full block text-center"
          >
            Property History
          </a>
        </div>
      </div>

      {/* Read-Only Work Order Details */}
      <div className="bg-white rounded-lg shadow mb-3">
        <div className="p-4">
          <div className="flex flex-wrap -mx-2 mb-3">
            <div className="w-full md:w-1/4 px-2 mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Type</label>
              <input 
                type="text" 
                value={workOrder.clientTypeName || workOrder.clientType || ''}
                readOnly
                className="border border-gray-300 rounded px-3 py-2 w-full bg-gray-100 text-gray-600 focus:outline-none"
              />
            </div>
            <div className="w-full md:w-1/4 px-2 mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
              <input
                type="text"
                value={workOrder.propertyTypeName || workOrder.propertyType || ''}
                readOnly
                className="border border-gray-300 rounded px-3 py-2 w-full bg-gray-100 text-gray-600 focus:outline-none"
              />
            </div>
            <div className="w-full md:w-1/4 px-2 mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Inspection Type</label>
              <input
                type="text"
                value={workOrder.inspectionTypeName || workOrder.inspectionType || ''}
                readOnly
                className="border border-gray-300 rounded px-3 py-2 w-full bg-gray-100 text-gray-600 focus:outline-none"
              />
            </div>
            <div className="w-full md:w-1/4 px-2 mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input 
                type="date" 
                value={workOrder.woDueDate ? formatDate(workOrder.woDueDate) : ''}
                readOnly
                className="border border-gray-300 rounded px-3 py-2 w-full bg-gray-100 text-gray-600 focus:outline-none"
              />
            </div>
            <div className="w-full md:w-1/4 px-2 mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date</label>
              <input 
                type="date" 
                value={workOrder.woScheduledDate ? formatDate(workOrder.woScheduledDate) : ''}
                readOnly
                className="border border-gray-300 rounded px-3 py-2 w-full bg-gray-100 text-gray-600 focus:outline-none"
              />
            </div>
            <div className="w-full md:w-1/4 px-2 mb-3 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1 truncate">Key No. / Pick Up</label>
              <div className="flex gap-1">
                <input
                  type="text"
                  name="keyNo"
                  value={formData.keyNo || ''}
                  onChange={handleInputChange}
                  disabled={!isJSASigned}
                  className={`border border-gray-300 rounded px-2 py-2 flex-1 min-w-0 focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="Key No."
                />
                <select
                  name="keyPickup"
                  value={formData.keyPickup === true ? 'true' : 'false'}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded px-1 py-2 w-14 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>
            <div className="w-full md:w-1/4 px-2 mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Requested Time</label>
              <input
                type="time"
                name="requestedTime"
                value={formData.requestedTime ? formatTime(formData.requestedTime) : ''}
                onChange={handleInputChange}
                disabled={!isJSASigned}
                className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
            </div>
            <div className="w-full md:w-1/4 px-2 mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Time</label>
              <input 
                type="time" 
                value={workOrder.scheduledTime ? formatTime(workOrder.scheduledTime) : ''}
                readOnly
                className="border border-gray-300 rounded px-3 py-2 w-full bg-gray-100 text-gray-600 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex flex-wrap -mx-2">
            <div className="w-full md:w-1/2 px-2 mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Work Order Comments</label>
              <textarea 
                name="woOrderNotes"
                value={formData.woOrderNotes || ''}
                rows={4}
                onChange={(e) => handleInputChange(e)}
                className="border border-gray-300 rounded px-3 py-2 w-full text-gray-600 focus:outline-none resize-vertical"
              />
            </div>
            <div className="w-full md:w-1/2 px-2 mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Notes</label>
              <textarea
                name="clientNotes"
                value={formData.clientNotes || ''}
                readOnly
                className="border border-gray-300 rounded px-3 py-2 w-full bg-gray-100 text-gray-600 focus:outline-none resize-vertical"
                rows={4}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Property Contact */}
      <div className="bg-white rounded-lg shadow mb-3">
        <div className="bg-gray-500 text-white p-4 rounded-t-lg">
          <h5 className="mb-0 text-lg font-medium">Property Contact</h5>
        </div>
        <div className="p-4">
          {contacts.length === 0 ? (
            <div className="text-center text-gray-500 py-4">
              No property contacts available
            </div>
          ) : (
            <>
              {/* Desktop table view */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700">Name</th>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700">Phone</th>
                      <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-700">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="border border-gray-300 px-4 py-2">
                          <input
                            type="text"
                            value={contact.tenantName || ''}
                            onChange={(e) => handleContactChange(index, 'tenantName', e.target.value)}
                            disabled={!isJSASigned}
                            className={`rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              !isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            placeholder="Enter name"
                          />
                        </td>
                        <td className="border border-gray-300 px-4 py-2">
                          <input
                            type="tel"
                            value={contact.tenantPhone || ''}
                            onChange={(e) => handleContactChange(index, 'tenantPhone', e.target.value)}
                            disabled={!isJSASigned}
                            className={`rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              !isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            placeholder="Enter phone number"
                          />
                        </td>
                        <td className="border border-gray-300 px-4 py-2">
                          <input
                            type="email"
                            value={contact.tenantEmail || ''}
                            onChange={(e) => handleContactChange(index, 'tenantEmail', e.target.value)}
                            disabled={!isJSASigned}
                            className={`rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              !isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''
                            }`}
                            placeholder="Enter email address"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile stacked view */}
              <div className="md:hidden space-y-4">
                {contacts.map((contact, index) => (
                  <div key={index} className="border border-gray-300 rounded-lg p-3 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={contact.tenantName || ''}
                        onChange={(e) => handleContactChange(index, 'tenantName', e.target.value)}
                        disabled={!isJSASigned}
                        className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        placeholder="Enter name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={contact.tenantPhone || ''}
                        onChange={(e) => handleContactChange(index, 'tenantPhone', e.target.value)}
                        disabled={!isJSASigned}
                        className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={contact.tenantEmail || ''}
                        onChange={(e) => handleContactChange(index, 'tenantEmail', e.target.value)}
                        disabled={!isJSASigned}
                        className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          !isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        placeholder="Enter email address"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Technicians Report */}
      <div className="bg-white rounded-lg shadow mb-3">
        <div className="bg-gray-500 text-white p-4 rounded-t-lg flex justify-between items-center">
          <h5 className="mb-0 text-lg font-medium">Technicians Report</h5>
        </div>
        <div className="p-4 bg-gray-50">
          
          {/* Time of Inspection */}
          <div className="flex flex-wrap -mx-2 mb-3">
            <div className="w-full md:w-1/2 px-2">
              <label className="block text-sm font-bold text-gray-700 mb-1">Time of Inspection</label>
              <input 
                type="time" 
                name="inspectionTime"
                value={formatTime(formData.inspectionTime)} 
                onChange={handleInputChange}
                disabled={true}
                className="border border-gray-300 rounded px-3 py-2 w-full bg-gray-100 cursor-not-allowed"
              />
            </div>
          </div>

          {/* On Arrival, Alarm Status, QR Code - All on one line */}
          <div className="flex flex-wrap -mx-2 mb-3">
            <div className="w-full md:w-1/3 px-2 mb-3">
              <label className="block text-sm font-bold text-gray-700 mb-1">On Arrival</label>
              <select
                name="onArrivalID_FK"
                value={formData.onArrivalID_FK || ''}
                onChange={handleInputChange}
                disabled={!isJSASigned}
                className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">Select Arrival...</option>
                {lookupData?.onArrivalOptions?.map(option => (
                  <option key={option.id} value={option.id}>{option.name}</option>
                ))}
              </select>
              
              {/* On Arrival Other - visible when "Other" is selected */}
              {(() => {
                const otherOption = lookupData?.onArrivalOptions?.find(o => 
                  o.name.toLowerCase().includes('other') || 
                  o.name.toLowerCase() === 'other' ||
                  o.name.toLowerCase().includes('specify')
                );
                
                const shouldShow = otherOption && (formData.onArrivalID_FK == otherOption.id);
                
                return shouldShow && (
                  <div className="mt-2">
                    <input 
                      type="text" 
                      name="onArrivalOther"
                      value={formData.onArrivalOther || ''} 
                      onChange={handleInputChange}
                      disabled={!isJSASigned}
                      placeholder="Please specify other"
                      className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                  </div>
                );
              })()}
            </div>
            
            <div className="w-full md:w-1/3 px-2 mb-3">
              <label className="block text-sm font-bold text-gray-700 mb-1">Alarm Status</label>
              <select
                name="alarmStatusID_FK"
                value={formData.alarmStatusID_FK || ''}
                onChange={handleInputChange}
                disabled={!isJSASigned}
                className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">Select Status...</option>
                {lookupData?.alarmStatusOptions?.map(option => (
                  <option key={option.id} value={option.id}>{option.name}</option>
                ))}
              </select>
              
              {/* Alarm Status Other - visible when "Other" is selected */}
              {(() => {
                const otherOption = lookupData?.alarmStatusOptions?.find(o => 
                  o.name.toLowerCase().includes('other') || 
                  o.name.toLowerCase() === 'other' ||
                  o.name.toLowerCase().includes('specify')
                );
                
                const shouldShow = otherOption && (formData.alarmStatusID_FK == otherOption.id);
                
                return shouldShow && (
                  <div className="mt-2">
                    <input 
                      type="text" 
                      name="alarmStatusOther"
                      value={formData.alarmStatusOther || ''} 
                      onChange={handleInputChange}
                      disabled={!isJSASigned}
                      placeholder="Please specify other status"
                      className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                  </div>
                );
              })()}
            </div>
            
            <div className="w-full md:w-1/3 px-2 mb-3">
              <label className="block text-sm font-bold text-gray-700 mb-1">QR Code Sticker</label>
              <select
                name="qrCodeID_FK"
                value={formData.qrCodeID_FK || ''}
                onChange={handleInputChange}
                disabled={!isJSASigned}
                className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                <option value="">Select QR Code...</option>
                {lookupData?.qrCodeOptions?.map(option => (
                  <option key={option.id} value={option.id}>{option.name}</option>
                ))}
              </select>
              
              {/* QR Code Other - visible when "Other" is selected */}
              {(() => {
                const otherOption = lookupData?.qrCodeOptions?.find(o => 
                  o.name.toLowerCase().includes('other') || 
                  o.name.toLowerCase() === 'other' ||
                  o.name.toLowerCase().includes('specify')
                );
                
                const shouldShow = otherOption && (formData.qrCodeID_FK == otherOption.id);
                
                return shouldShow && (
                  <div className="mt-2">
                    <input 
                      type="text" 
                      name="qrCodeOther"
                      value={formData.qrCodeOther || ''} 
                      onChange={handleInputChange}
                      disabled={!isJSASigned}
                      placeholder="Please specify other QR code"
                      className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    />
                  </div>
                );
              })()}
            </div>
          </div>

        </div>
      </div>

      {/* Safety Switch Section */}
      <div className="flex flex-wrap -mx-2 mb-4">
            <div className="w-full px-2">
              <div className="bg-white rounded-lg shadow border border-gray-300">
                <div className="p-4">
                  <h6 className="text-blue-600 font-semibold mb-3">Safety Switch</h6>
                  
                  {/* Safety Switch Test Required and Location on one line */}
                  <div className="flex flex-wrap -mx-2 mb-4">
                    <div className="w-full md:w-1/2 px-2 mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Safety Switch Test Required</label>
                      <div className="flex items-center space-x-4 mb-2">
                        <label className="flex items-center">
                          <input 
                            type="radio" 
                            name="safetySwitchReq" 
                            value="true"
                            checked={formData.safetySwitchReq === true}
                            onChange={handleInputChange}
                            disabled={!isJSASigned}
                            className={`mr-2 ${!isJSASigned ? 'cursor-not-allowed' : ''}`}
                          />
                          <span className={`text-sm ${!isJSASigned ? 'text-gray-500' : ''}`}>Yes</span>
                        </label>
                        <label className="flex items-center">
                          <input 
                            type="radio" 
                            name="safetySwitchReq" 
                            value="false"
                            checked={formData.safetySwitchReq === false}
                            onChange={handleInputChange}
                            disabled={!isJSASigned}
                            className={`mr-2 ${!isJSASigned ? 'cursor-not-allowed' : ''}`}
                          />
                          <span className={`text-sm ${!isJSASigned ? 'text-gray-500' : ''}`}>No</span>
                        </label>
                      </div>
                      
                      {/* Safety Switch Pass/Fail - only visible when Test Required is Yes */}
                      {formData.safetySwitchReq === true && (
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center">
                            <input 
                              type="radio" 
                              name="safetySwitchPass" 
                              value="true"
                              checked={formData.safetySwitchPass === true}
                              onChange={handleInputChange}
                              disabled={!isJSASigned}
                              className={`mr-2 ${!isJSASigned ? 'cursor-not-allowed' : ''}`}
                            />
                            <span className={`text-sm ${!isJSASigned ? 'text-gray-500' : ''}`}>Pass</span>
                          </label>
                          <label className="flex items-center">
                            <input 
                              type="radio" 
                              name="safetySwitchPass" 
                              value="false"
                              checked={formData.safetySwitchPass === false}
                              onChange={handleInputChange}
                              disabled={!isJSASigned}
                              className={`mr-2 ${!isJSASigned ? 'cursor-not-allowed' : ''}`}
                            />
                            <span className={`text-sm ${!isJSASigned ? 'text-gray-500' : ''}`}>Fail</span>
                          </label>
                        </div>
                      )}
                    </div>

                    <div className="w-full md:w-1/2 px-2 mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      <select 
                        name="safetySwitchLocationID_FK"
                        value={formData.safetySwitchLocationID_FK || ''} 
                        onChange={handleInputChange}
                        disabled={!isJSASigned}
                        className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      >
                        <option value="">-- Select --</option>
                        {lookupData?.safetySwitchLocations?.map(location => (
                          <option key={location.id} value={location.id}>{location.name}</option>
                        ))}
                      </select>
                      
                      {/* Safety Switch Location Other - visible when "Other" is selected */}
                      {(() => {
                        const otherLocation = lookupData?.safetySwitchLocations?.find(l => 
                          l.name.toLowerCase().includes('other') || 
                          l.name.toLowerCase() === 'other' ||
                          l.name.toLowerCase().includes('specify')
                        );
                        
                        const shouldShow = otherLocation && (formData.safetySwitchLocationID_FK == otherLocation.id);
                        
                        return shouldShow && (
                          <div className="mt-2">
                            <input 
                              type="text" 
                              name="safetySwitchLocationOther"
                              value={formData.safetySwitchLocationOther || ''} 
                              onChange={handleInputChange}
                              disabled={!isJSASigned}
                              placeholder="Please specify other location"
                              className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Safety Switch Notes and Unable to Test on one line */}
                  <div className="flex flex-wrap -mx-2 mb-4">
                    <div className="w-full md:w-2/3 px-2 mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea 
                        name="safetySwitchNotes"
                        value={formData.safetySwitchNotes || ''} 
                        onChange={handleInputChange}
                        className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500"
                        rows={3}
                        placeholder="Enter safety switch notes..."
                      />
                    </div>

                    <div className="w-full md:w-1/3 px-2 mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unable to Test</label>
                      <select 
                        name="unableToTestReasonID_FK"
                        value={formData.unableToTestReasonID_FK || ''} 
                        onChange={handleInputChange}
                        disabled={!isJSASigned}
                        className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500 ${
                          !isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      >
                        <option value="">-- Select --</option>
                        {lookupData?.unableToTestReasons?.map(reason => (
                          <option key={reason.id} value={reason.id}>{reason.name}</option>
                        ))}
                      </select>
                      
                      {/* Unable to Test Other - visible when "Other" is selected */}
                      {(() => {
                        const otherReason = lookupData?.unableToTestReasons?.find(r => 
                          r.name.toLowerCase().includes('other') || 
                          r.name.toLowerCase() === 'other' ||
                          r.name.toLowerCase().includes('specify')
                        );
                        
                        const shouldShow = otherReason && (formData.unableToTestReasonID_FK == otherReason.id);
                        
                        return shouldShow && (
                          <div className="mt-2">
                            <input 
                              type="text" 
                              name="unableToTestReasonOther"
                              value={formData.unableToTestReasonOther || ''} 
                              onChange={handleInputChange}
                              disabled={!isJSASigned}
                              placeholder="Please specify other reason"
                              className={`border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

      {/* Corded Blinds Section */}
      <div className="flex flex-wrap -mx-2 mb-4">
        <div className="w-full px-2">
          <div className="bg-white rounded-lg shadow border border-gray-300">
            <div className="p-4">
              <h6 className="text-blue-600 font-semibold mb-3">Corded Blinds</h6>
              
              {/* Corded Blinds Required Radio Buttons */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Corded Blinds Required</label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input 
                      type="radio" 
                      name="cordedBlinkReq" 
                      value="true"
                      checked={formData.cordedBlinkReq === true}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm">Yes</span>
                  </label>
                  <label className="flex items-center">
                    <input 
                      type="radio" 
                      name="cordedBlinkReq" 
                      value="false"
                      checked={formData.cordedBlinkReq === false}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm">No</span>
                  </label>
                </div>
              </div>

              {/* Corded Blinds Details - Only show when required */}
              {formData.cordedBlinkReq === true && (
                <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Total Blinds</label>
                      <input 
                        type="number" 
                        name="blindsNo"
                        value={formData.blindsNo || '0'} 
                        onChange={handleInputChange}
                        onFocus={(e) => e.target.select()}
                        className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cleats Installed</label>
                      <input 
                        type="number" 
                        name="cleatsNo"
                        value={formData.cleatsNo || '0'} 
                        onChange={handleInputChange}
                        onFocus={(e) => e.target.select()}
                        pattern="[0-9]*"
                        className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Warning Tags</label>
                      <input 
                        type="number" 
                        name="tagsNo"
                        value={formData.tagsNo || '0'} 
                        onChange={handleInputChange}
                        onFocus={(e) => e.target.select()}
                        pattern="[0-9]*"
                        className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Non Compliant</label>
                      <input 
                        type="number" 
                        name="ncBlinds"
                        value={formData.ncBlinds || '0'} 
                        onChange={handleInputChange}
                        onFocus={(e) => e.target.select()}
                        pattern="[0-9]*"
                        className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Corded Blinds Notes/Reason for Non Compliance</label>
                    <textarea
                      name="cordedBlindNotes"
                      value={formData.cordedBlindNotes || ''}
                      onChange={handleInputChange}
                      className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500"
                      rows={3}
                      placeholder="Enter notes or reason for non compliance..."
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Notes & Compliance */}
      <div className="bg-white rounded-lg shadow mb-3">
        <div className="bg-gray-500 text-white p-4 rounded-t-lg">
            <h5 className="mb-0 text-lg font-medium">Notes & Compliance</h5>
        </div>
        <div className="p-4 bg-gray-50">
            <div className="flex flex-wrap -mx-2 mb-3">
                <div className="w-full md:w-1/2 px-2">
                    <label className="block text-sm font-bold text-gray-500 mb-1">Upgrade completed by CSA</label>
                    <div>
                        <label className="inline-flex items-center">
                            <input
                                type="radio"
                                name="upgradedCSA"
                                value="true"
                                checked={formData.upgradedCSA === true}
                                disabled
                                className="form-radio cursor-not-allowed"
                            />
                            <span className="ml-2 text-gray-500">Yes</span>
                        </label>
                        <label className="inline-flex items-center ml-6">
                            <input
                                type="radio"
                                name="upgradedCSA"
                                value="false"
                                checked={formData.upgradedCSA === false || formData.upgradedCSA === null || formData.upgradedCSA === undefined}
                                disabled
                                className="form-radio cursor-not-allowed"
                            />
                            <span className="ml-2 text-gray-500">No</span>
                        </label>
                    </div>
                </div>
                <div className="w-full md:w-1/2 px-2">
                    <label className="block text-sm font-bold text-gray-700 mb-1">Suspected Asbestos</label>
                    <div>
                        <label className="inline-flex items-center">
                            <input
                                type="radio"
                                name="susAsbestos"
                                value="true"
                                checked={formData.susAsbestos === true}
                                onChange={handleInputChange}
                                className="form-radio"
                            />
                            <span className="ml-2">Yes</span>
                        </label>
                        <label className="inline-flex items-center ml-6">
                            <input
                                type="radio"
                                name="susAsbestos"
                                value="false"
                                checked={formData.susAsbestos === false}
                                onChange={handleInputChange}
                                className="form-radio"
                            />
                            <span className="ml-2">No</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* Existing Alarms Management */}
      <div className="bg-white rounded-lg shadow mb-3">
        <div className="bg-blue-600 text-white p-4 rounded-t-lg flex justify-between items-center">
          <h5 className="mb-0 text-lg font-medium">Existing Alarms</h5>
          <div className="flex gap-2">
            <button 
              type="button"
              onClick={handleImportAlarms}
              disabled={!isJSASigned || !formData.clientPropertyID_FK}
              className="bg-orange-500 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-1 px-3 rounded text-sm"
              title={!formData.clientPropertyID_FK ? "Property ID required to import alarms" : !isJSASigned ? "JSA must be signed to import alarms" : "Import alarms from previous work order"}
            >
              📥 Import Alarms
            </button>
            <button
              type="button"
              onClick={() => {
                const newAlarm: AlarmDto = {
                  workOrderID_FK: workOrderId,
                  alarmTypeID_FK: undefined,
                  alarmLocationID_FK: undefined,
                  alarmBrandID_FK: undefined,
                  alarmRFRID_FK: undefined,
                  alarmInstallationID_FK: undefined,
                  sensorID_FK: undefined,
                  sensor: "2",
                  dbID_FK: undefined,
                  battID_FK: undefined,
                  expiryMonth: undefined,
                  expiryYear: undefined,
                  isExisting: true,
                  offlineId: generateUUID()
                }
                const newAlarmKey = `existing-${existingAlarms.length}`
                updateExistingAlarms(sortAlarms([...existingAlarms, newAlarm]))
                // Put new alarm in edit mode immediately
                setAlarmEditStates(prev => ({ ...prev, [newAlarmKey]: true }))
              }}
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded text-sm"
            >
              + Add Existing Alarm
            </button>
          </div>
        </div>
        <div className="p-4">
          {existingAlarms.length === 0 ? (
            <div className="text-center text-gray-500 py-4">
              No existing alarms added. Click "Add Existing Alarm" to get started.
            </div>
          ) : (
            <>
              {/* Column Headers */}
              <div className="bg-gray-200 border border-gray-300 rounded-t-lg px-3 py-2 mb-0">
                <div className="flex items-center gap-2 flex-wrap text-xs font-bold text-gray-700">
                  <div className="flex-1 min-w-[100px]">Type</div>
                  <div className="flex-1 min-w-[55px]">Exp. M</div>
                  <div className="flex-1 min-w-[50px]">Exp. Y</div>
                  <div className="flex-1 min-w-[100px]">Location</div>
                  <div className="flex-1 min-w-[100px]">Brand</div>
                  <div className="flex-1 min-w-[50px]">DB</div>
                  <div className="flex-1 min-w-[50px]">Batt%</div>
                  <div className="flex-1 min-w-[55px]">RFR</div>
                  <div className="w-[200px]">Actions</div>
                </div>
              </div>

              {/* Alarm Rows */}
              {existingAlarms.filter(alarm => !alarm.deleted).map((alarm, index) => (
                <div
                  key={index}
                  className={`border border-t-0 border-gray-300 px-3 py-3 ${
                    alarmEditStates[`existing-${index}`] ? 'bg-blue-50' : 'bg-gray-50'
                  } ${index === existingAlarms.filter(a => !a.deleted).length - 1 ? 'rounded-b-lg' : ''}`}
                >
                  {/* All fields and buttons in one row */}
                  <div className="flex items-center gap-2 flex-wrap">
                  {/* Type */}
                  <div className="flex-1 min-w-[100px]">
                    {!alarmEditStates[`existing-${index}`] ? (
                      <span className="block px-2 py-2 text-xs bg-gray-100 border border-gray-300 rounded break-words">
                        {lookupData?.alarmTypes?.find(t => t.id === alarm.alarmTypeID_FK)?.name || '-'}
                      </span>
                    ) : (
                      <select
                        value={alarm.alarmTypeID_FK || ''}
                        onChange={(e) => {
                          if (!isJSASigned) return
                          const updated = [...existingAlarms]
                          updated[index] = { ...alarm, alarmTypeID_FK: e.target.value ? Number(e.target.value) : undefined }
                          updateExistingAlarms(updated)
                        }}
                        disabled={!isJSASigned}
                        className={`appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      >
                        <option value="">Select...</option>
                        {lookupData?.alarmTypes?.map(type => (
                          <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Sensor - Hidden field, always set to PE */}
                  <input type="hidden" value="2" />

                  {/* Exp. M */}
                  <div className="flex-1 min-w-[55px]">
                    <select
                      value={alarm.expiryM || ''}
                      onChange={(e) => {
                        if (!isJSASigned || !alarmEditStates[`existing-${index}`]) return
                        const updated = [...existingAlarms]
                        updated[index] = { ...alarm, expiryM: e.target.value }
                        updateExistingAlarms(updated)
                      }}
                      disabled={!isJSASigned || !alarmEditStates[`existing-${index}`]}
                      className={`appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500 ${(!isJSASigned || !alarmEditStates[`existing-${index}`]) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <option value="">M...</option>
                      {(lookupData?.expiryMonthOptions ?? []).map((option) => (
                        <option key={option.id} value={option.name.substring(0,3)}>{option.name.substring(0,3)}</option>
                      ))}
                    </select>
                  </div>

                  {/* Exp. Y */}
                  <div className="flex-1 min-w-[50px]">
                    <select
                      value={alarm.expiryY || ''}
                      onChange={(e) => {
                        if (!isJSASigned || !alarmEditStates[`existing-${index}`]) return
                        const updated = [...existingAlarms]
                        updated[index] = { ...alarm, expiryY: e.target.value }
                        updateExistingAlarms(updated)
                      }}
                      disabled={!isJSASigned || !alarmEditStates[`existing-${index}`]}
                      className={`appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500 ${(!isJSASigned || !alarmEditStates[`existing-${index}`]) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <option value="">Y...</option>
                      {(lookupData?.expiryYearOptions ?? []).map((option) => (
                        <option key={option.id} value={option.name}>{option.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Location */}
                  <div className="flex-1 min-w-[100px]">
                    {(() => {
                      const otherLocation = lookupData?.alarmLocations?.find(l =>
                        l.name.toLowerCase().includes('other') ||
                        l.name.toLowerCase() === 'other' ||
                        l.name.toLowerCase().includes('specify')
                      );
                      const isOtherSelected = otherLocation && (alarm.alarmLocationID_FK == otherLocation.id);
                      const isEditMode = alarmEditStates[`existing-${index}`];

                      // View mode - show span with value
                      if (!isEditMode) {
                        const locationName = isOtherSelected
                          ? (alarm.alarmLocationOther || 'Other')
                          : (lookupData?.alarmLocations?.find(l => l.id === alarm.alarmLocationID_FK)?.name || '-');
                        return (
                          <span className="block px-2 py-2 text-xs bg-gray-100 border border-gray-300 rounded break-words">{locationName}</span>
                        );
                      }

                      // Edit mode - show dropdown (and text input if Other is selected)
                      return (
                        <>
                          <select
                            value={alarm.alarmLocationID_FK || ''}
                            onChange={(e) => {
                              if (!isJSASigned) return
                              const updated = [...existingAlarms]
                              updated[index] = { ...alarm, alarmLocationID_FK: e.target.value ? Number(e.target.value) : undefined }
                              updateExistingAlarms(updated)
                            }}
                            disabled={!isJSASigned}
                            className={`appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          >
                            <option value="">Select...</option>
                            {lookupData?.alarmLocations?.map(location => (
                              <option key={location.id} value={location.id}>{location.name}</option>
                            ))}
                          </select>
                          {isOtherSelected && (
                            <input
                              type="text"
                              value={alarm.alarmLocationOther || ''}
                              onChange={(e) => {
                                if (!isJSASigned) return
                                const updated = [...existingAlarms]
                                updated[index] = { ...alarm, alarmLocationOther: e.target.value }
                                updateExistingAlarms(updated)
                              }}
                              disabled={!isJSASigned}
                              placeholder="Specify location"
                              className={`border border-gray-300 rounded px-2 py-2 w-full text-sm mt-1 focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Brand */}
                  <div className="flex-1 min-w-[100px]">
                    {(() => {
                      const otherBrand = lookupData?.alarmBrands?.find(b =>
                        b.name.toLowerCase().includes('other') ||
                        b.name.toLowerCase() === 'other' ||
                        b.name.toLowerCase().includes('specify')
                      );
                      const isOtherSelected = otherBrand && (alarm.alarmBrandID_FK == otherBrand.id);
                      const isEditMode = alarmEditStates[`existing-${index}`];

                      // View mode - show span with value
                      if (!isEditMode) {
                        const brandName = isOtherSelected
                          ? (alarm.alarmBrandOther || 'Other')
                          : (lookupData?.alarmBrands?.find(b => b.id === alarm.alarmBrandID_FK)?.name || '-');
                        return (
                          <span className="block px-2 py-2 text-xs bg-gray-100 border border-gray-300 rounded break-words">{brandName}</span>
                        );
                      }

                      // Edit mode - show dropdown (and text input if Other is selected)
                      return (
                        <>
                          <select
                            value={alarm.alarmBrandID_FK || ''}
                            onChange={(e) => {
                              if (!isJSASigned) return
                              const updated = [...existingAlarms]
                              updated[index] = { ...alarm, alarmBrandID_FK: e.target.value ? Number(e.target.value) : undefined }
                              updateExistingAlarms(updated)
                            }}
                            disabled={!isJSASigned}
                            className={`appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          >
                            <option value="">Select...</option>
                            {lookupData?.alarmBrands?.map(brand => (
                              <option key={brand.id} value={brand.id}>{brand.name}</option>
                            ))}
                          </select>
                          {isOtherSelected && (
                            <input
                              type="text"
                              value={alarm.alarmBrandOther || ''}
                              onChange={(e) => {
                                if (!isJSASigned) return
                                const updated = [...existingAlarms]
                                updated[index] = { ...alarm, alarmBrandOther: e.target.value }
                                updateExistingAlarms(updated)
                              }}
                              disabled={!isJSASigned}
                              placeholder="Specify brand"
                              className={`border border-gray-300 rounded px-2 py-2 w-full text-sm mt-1 focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* DB */}
                  <div className="flex-1 min-w-[50px]">
                    <select
                      value={alarm.db || ''}
                      onChange={(e) => {
                        if (!isJSASigned || !alarmEditStates[`existing-${index}`]) return
                        const updated = [...existingAlarms]
                        updated[index] = { ...alarm, db: e.target.value }
                        updateExistingAlarms(updated)
                      }}
                      disabled={!isJSASigned || !alarmEditStates[`existing-${index}`]}
                      className={`appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500 ${(!isJSASigned || !alarmEditStates[`existing-${index}`]) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <option value="">DB...</option>
                      {lookupData?.dbOptions?.map(db => (
                        <option key={db.id} value={db.id.toString()}>{db.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Batt% */}
                  <div className="flex-1 min-w-[50px]">
                    <select
                      value={alarm.batt || ''}
                      onChange={(e) => {
                        if (!isJSASigned || !alarmEditStates[`existing-${index}`]) return
                        const updated = [...existingAlarms]
                        updated[index] = { ...alarm, batt: e.target.value }
                        updateExistingAlarms(updated)
                      }}
                      disabled={!isJSASigned || !alarmEditStates[`existing-${index}`]}
                      className={`appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500 ${(!isJSASigned || !alarmEditStates[`existing-${index}`]) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <option value="">%...</option>
                      {lookupData?.battOptions?.map(batt => (
                        <option key={batt.id} value={batt.id.toString()}>{batt.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* RFR */}
                  <div className="flex-1 min-w-[55px]">
                    {!alarmEditStates[`existing-${index}`] ? (
                      <span className="block px-2 py-2 text-xs bg-gray-100 border border-gray-300 rounded break-words">
                        {lookupData?.alarmRFROptions?.find(r => r.id === alarm.alarmRFRID_FK)?.name || '-'}
                      </span>
                    ) : (
                      <select
                        value={alarm.alarmRFRID_FK || ''}
                        onChange={(e) => {
                          if (!isJSASigned) return
                          const updated = [...existingAlarms]
                          updated[index] = { ...alarm, alarmRFRID_FK: e.target.value ? Number(e.target.value) : undefined }
                          updateExistingAlarms(updated)
                        }}
                        disabled={!isJSASigned}
                        className={`appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500 ${!isJSASigned ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                      >
                        <option value="">RFR...</option>
                        {lookupData?.alarmRFROptions?.map(rfr => (
                          <option key={rfr.id} value={rfr.id}>{rfr.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="w-[200px] flex gap-1 justify-end">
                    <button
                      type="button"
                      onClick={() => handleViewImages(alarm.workOrderAlarmID || alarm.offlineId || 0)}
                      className="hover:bg-gray-100 p-2 rounded text-xs border border-gray-300"
                      title="View Images"
                    >
                      👁️{alarmPhotoCounts[(alarm.workOrderAlarmID || alarm.offlineId || '').toString()] > 0 ? ` ${alarmPhotoCounts[(alarm.workOrderAlarmID || alarm.offlineId || '').toString()]}` : ''}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUploadImages(alarm.workOrderAlarmID || alarm.offlineId || 0)}
                      disabled={!alarm.workOrderAlarmID && !alarm.offlineId}
                      className="hover:bg-gray-100 p-2 rounded text-xs border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Upload Images"
                    >
                      📷
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCopyAlarm('existing', index)}
                      className="hover:bg-gray-100 p-2 rounded text-xs border border-gray-300"
                      title="Copy Alarm (excludes location)"
                    >
                      📋
                    </button>
                    {alarmEditStates[`existing-${index}`] ? (
                      <button
                        type="button"
                        onClick={() => handleSaveAlarm('existing', index)}
                        className="hover:bg-blue-100 p-2 rounded text-xs border border-blue-500 bg-blue-50"
                        title="Save Alarm"
                      >
                        💾
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => handleEditAlarm('existing', index)}
                          className="hover:bg-gray-100 p-2 rounded text-xs border border-gray-300"
                          title="Edit Alarm"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (window.confirm('You are about to delete this alarm. Are you sure?')) {
                              let updated: AlarmDto[]
                              if (alarm.workOrderAlarmID) {
                                updated = [...existingAlarms]
                                updated[index] = { ...alarm, deleted: true }
                              } else {
                                updated = existingAlarms.filter((_, i) => i !== index)
                              }
                              updateExistingAlarms(updated)
                              // Auto-save with the updated array
                              await handleSaveWorkOrder(updated, undefined)
                            }
                          }}
                          className="hover:bg-red-100 p-2 rounded text-xs border border-red-300 text-red-600"
                          title="Remove"
                        >
                          ❌
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
            </>
          )}
        </div>
      </div>

      {/* New Alarms Management */}
      <div className="bg-white rounded-lg shadow mb-3">
        <div className="bg-green-600 text-white p-4 rounded-t-lg flex justify-between items-center">
          <h5 className="mb-0 text-lg font-medium">New Alarms</h5>
          <button 
            type="button"
            onClick={() => {
              const newAlarm: AlarmDto = {
                workOrderID_FK: workOrderId,
                alarmTypeID_FK: undefined,
                alarmLocationID_FK: undefined,
                alarmBrandID_FK: undefined,
                alarmInstallationID_FK: undefined,
                sensorID_FK: undefined,
                sensor: "2",
                dbID_FK: undefined,
                battID_FK: undefined,
                expiryMonth: undefined,
                expiryYear: undefined,
                isExisting: false,
                offlineId: generateUUID()
              }
              const newAlarmKey = `new-${newAlarms.length}`
              updateNewAlarms(sortAlarms([...newAlarms, newAlarm]))
              // Put new alarm in edit mode immediately
              setAlarmEditStates(prev => ({ ...prev, [newAlarmKey]: true }))
            }}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm"
          >
            + Add New Alarm
          </button>
        </div>
        <div className="p-4">
          {newAlarms.length === 0 ? (
            <div className="text-center text-gray-500 py-4">
              No new alarms added. Click "Add New Alarm" to get started.
            </div>
          ) : (
            <>
              {/* Column Headers */}
              <div className="bg-gray-200 border border-gray-300 rounded-t-lg px-3 py-2 mb-0">
                <div className="flex items-center gap-2 flex-wrap text-xs font-bold text-gray-700">
                  <div className="flex-1 min-w-[100px]">Type</div>
                  <div className="flex-1 min-w-[55px]">Exp. M</div>
                  <div className="flex-1 min-w-[50px]">Exp. Y</div>
                  <div className="flex-1 min-w-[100px]">Location</div>
                  <div className="flex-1 min-w-[100px]">Brand</div>
                  <div className="flex-1 min-w-[50px]">DB</div>
                  <div className="flex-1 min-w-[50px]">Batt%</div>
                  <div className="flex-1 min-w-[55px]">Installation</div>
                  <div className="w-[200px]">Actions</div>
                </div>
              </div>

              {/* Alarm Rows */}
              {newAlarms.filter(alarm => !alarm.deleted).map((alarm, index) => (
                <div
                  key={index}
                  className={`border border-t-0 border-gray-300 px-3 py-3 ${
                    alarmEditStates[`new-${index}`] ? 'bg-blue-50' : 'bg-gray-50'
                  } ${index === newAlarms.filter(a => !a.deleted).length - 1 ? 'rounded-b-lg' : ''}`}
                >
                  {/* All fields and buttons in one row */}
                  <div className="flex items-center gap-2 flex-wrap">
                  {/* Type */}
                  <div className="flex-1 min-w-[100px]">
                    {!alarmEditStates[`new-${index}`] ? (
                      <span className="block px-2 py-2 text-xs bg-gray-100 border border-gray-300 rounded break-words">
                        {lookupData?.alarmTypes?.find(t => t.id === alarm.alarmTypeID_FK)?.name || '-'}
                      </span>
                    ) : (
                      <select
                        value={alarm.alarmTypeID_FK || ''}
                        onChange={(e) => {
                          const updated = [...newAlarms]
                          updated[index] = { ...alarm, alarmTypeID_FK: e.target.value ? Number(e.target.value) : undefined }
                          updateNewAlarms(updated)
                        }}
                        className="appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500"
                      >
                        <option value="">Select...</option>
                        {lookupData?.alarmTypes?.map(type => (
                          <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Sensor - Hidden field, always set to PE */}
                  <input type="hidden" value="2" />

                  {/* Exp. M */}
                  <div className="flex-1 min-w-[55px]">
                    <select
                      value={alarm.expiryM || ''}
                      onChange={(e) => {
                        if (!alarmEditStates[`new-${index}`]) return
                        const updated = [...newAlarms]
                        updated[index] = { ...alarm, expiryM: e.target.value }
                        updateNewAlarms(updated)
                      }}
                      disabled={!alarmEditStates[`new-${index}`]}
                      className={`appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500 ${!alarmEditStates[`new-${index}`] ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <option value="">M...</option>
                      {(lookupData?.expiryMonthOptions ?? []).map((option) => (
                        <option key={option.id} value={option.name.substring(0,3)}>{option.name.substring(0,3)}</option>
                      ))}
                    </select>
                  </div>

                  {/* Exp. Y */}
                  <div className="flex-1 min-w-[50px]">
                    <select
                      value={alarm.expiryY || ''}
                      onChange={(e) => {
                        if (!alarmEditStates[`new-${index}`]) return
                        const updated = [...newAlarms]
                        updated[index] = { ...alarm, expiryY: e.target.value }
                        updateNewAlarms(updated)
                      }}
                      disabled={!alarmEditStates[`new-${index}`]}
                      className={`appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500 ${!alarmEditStates[`new-${index}`] ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <option value="">Y...</option>
                      {(lookupData?.expiryYearOptions ?? []).map((option) => (
                        <option key={option.id} value={option.name}>{option.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Location */}
                  <div className="flex-1 min-w-[100px]">
                    {(() => {
                      const otherLocation = lookupData?.alarmLocations?.find(l =>
                        l.name.toLowerCase().includes('other') ||
                        l.name.toLowerCase() === 'other' ||
                        l.name.toLowerCase().includes('specify')
                      );
                      const isOtherSelected = otherLocation && (alarm.alarmLocationID_FK == otherLocation.id);
                      const isEditMode = alarmEditStates[`new-${index}`];

                      // View mode - show span with value
                      if (!isEditMode) {
                        const locationName = isOtherSelected
                          ? (alarm.alarmLocationOther || 'Other')
                          : (lookupData?.alarmLocations?.find(l => l.id === alarm.alarmLocationID_FK)?.name || '-');
                        return (
                          <span className="block px-2 py-2 text-xs bg-gray-100 border border-gray-300 rounded break-words">{locationName}</span>
                        );
                      }

                      // Edit mode - show dropdown (and text input if Other is selected)
                      return (
                        <>
                          <select
                            value={alarm.alarmLocationID_FK || ''}
                            onChange={(e) => {
                              const updated = [...newAlarms]
                              updated[index] = { ...alarm, alarmLocationID_FK: e.target.value ? Number(e.target.value) : undefined }
                              updateNewAlarms(updated)
                            }}
                            className="appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500"
                          >
                            <option value="">Select...</option>
                            {lookupData?.alarmLocations?.map(location => (
                              <option key={location.id} value={location.id}>{location.name}</option>
                            ))}
                          </select>
                          {isOtherSelected && (
                            <input
                              type="text"
                              value={alarm.alarmLocationOther || ''}
                              onChange={(e) => {
                                const updated = [...newAlarms]
                                updated[index] = { ...alarm, alarmLocationOther: e.target.value }
                                updateNewAlarms(updated)
                              }}
                              placeholder="Specify location"
                              className="border border-gray-300 rounded px-2 py-2 w-full text-sm mt-1 focus:outline-none focus:border-blue-500"
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Brand */}
                  <div className="flex-1 min-w-[100px]">
                    {(() => {
                      const otherBrand = lookupData?.alarmBrands?.find(b =>
                        b.name.toLowerCase().includes('other') ||
                        b.name.toLowerCase() === 'other' ||
                        b.name.toLowerCase().includes('specify')
                      );
                      const isOtherSelected = otherBrand && (alarm.alarmBrandID_FK == otherBrand.id);
                      const isEditMode = alarmEditStates[`new-${index}`];

                      // View mode - show span with value
                      if (!isEditMode) {
                        const brandName = isOtherSelected
                          ? (alarm.alarmBrandOther || 'Other')
                          : (lookupData?.alarmBrands?.find(b => b.id === alarm.alarmBrandID_FK)?.name || '-');
                        return (
                          <span className="block px-2 py-2 text-xs bg-gray-100 border border-gray-300 rounded break-words">{brandName}</span>
                        );
                      }

                      // Edit mode - show dropdown (and text input if Other is selected)
                      return (
                        <>
                          <select
                            value={alarm.alarmBrandID_FK || ''}
                            onChange={(e) => {
                              const updated = [...newAlarms]
                              updated[index] = { ...alarm, alarmBrandID_FK: e.target.value ? Number(e.target.value) : undefined }
                              updateNewAlarms(updated)
                            }}
                            className="appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500"
                          >
                            <option value="">Select...</option>
                            {lookupData?.alarmBrands?.map(brand => (
                              <option key={brand.id} value={brand.id}>{brand.name}</option>
                            ))}
                          </select>
                          {isOtherSelected && (
                            <input
                              type="text"
                              value={alarm.alarmBrandOther || ''}
                              onChange={(e) => {
                                const updated = [...newAlarms]
                                updated[index] = { ...alarm, alarmBrandOther: e.target.value }
                                updateNewAlarms(updated)
                              }}
                              placeholder="Specify brand"
                              className="border border-gray-300 rounded px-2 py-2 w-full text-sm mt-1 focus:outline-none focus:border-blue-500"
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* DB */}
                  <div className="flex-1 min-w-[50px]">
                    <select
                      value={alarm.db || ''}
                      onChange={(e) => {
                        if (!alarmEditStates[`new-${index}`]) return
                        const updated = [...newAlarms]
                        updated[index] = { ...alarm, db: e.target.value }
                        updateNewAlarms(updated)
                      }}
                      disabled={!alarmEditStates[`new-${index}`]}
                      className={`appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500 ${!alarmEditStates[`new-${index}`] ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <option value="">DB...</option>
                      {lookupData?.dbOptions?.map(db => (
                        <option key={db.id} value={db.id.toString()}>{db.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Batt% */}
                  <div className="flex-1 min-w-[50px]">
                    <select
                      value={alarm.batt || ''}
                      onChange={(e) => {
                        if (!alarmEditStates[`new-${index}`]) return
                        const updated = [...newAlarms]
                        updated[index] = { ...alarm, batt: e.target.value }
                        updateNewAlarms(updated)
                      }}
                      disabled={!alarmEditStates[`new-${index}`]}
                      className={`appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500 ${!alarmEditStates[`new-${index}`] ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <option value="">%...</option>
                      {lookupData?.battOptions?.map(batt => (
                        <option key={batt.id} value={batt.id.toString()}>{batt.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Installation */}
                  <div className="flex-1 min-w-[55px]">
                    {!alarmEditStates[`new-${index}`] ? (
                      <span className="block px-2 py-2 text-xs bg-gray-100 border border-gray-300 rounded break-words">
                        {lookupData?.alarmInstallationOptions?.find(i => i.id === alarm.alarmNewInstallationID_FK)?.name || '-'}
                      </span>
                    ) : (
                      <select
                        value={alarm.alarmNewInstallationID_FK || ''}
                        onChange={(e) => {
                          const updated = [...newAlarms]
                          updated[index] = { ...alarm, alarmNewInstallationID_FK: e.target.value ? Number(e.target.value) : undefined }
                          updateNewAlarms(updated)
                        }}
                        className="appearance-none border border-gray-300 rounded px-2 py-2 w-full text-sm focus:outline-none focus:border-blue-500"
                      >
                        <option value="">Install...</option>
                        {lookupData?.alarmInstallationOptions?.map(installation => (
                          <option key={installation.id} value={installation.id}>{installation.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="w-[200px] flex gap-1 justify-end">
                    <button
                      type="button"
                      onClick={() => handleViewImages(alarm.workOrderAlarmID || alarm.offlineId || 0)}
                      className="hover:bg-gray-100 p-2 rounded text-xs border border-gray-300"
                      title="View Images"
                    >
                      👁️{alarmPhotoCounts[(alarm.workOrderAlarmID || alarm.offlineId || '').toString()] > 0 ? ` ${alarmPhotoCounts[(alarm.workOrderAlarmID || alarm.offlineId || '').toString()]}` : ''}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUploadImages(alarm.workOrderAlarmID || alarm.offlineId || 0)}
                      disabled={!alarm.workOrderAlarmID && !alarm.offlineId}
                      className="hover:bg-gray-100 p-2 rounded text-xs border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Upload Images"
                    >
                      📷
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCopyAlarm('new', index)}
                      className="hover:bg-gray-100 p-2 rounded text-xs border border-gray-300"
                      title="Copy Alarm (excludes location)"
                    >
                      📋
                    </button>
                    {alarmEditStates[`new-${index}`] ? (
                      <button
                        type="button"
                        onClick={() => handleSaveAlarm('new', index)}
                        className="hover:bg-blue-100 p-2 rounded text-xs border border-blue-500 bg-blue-50"
                        title="Save Alarm"
                      >
                        💾
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => handleEditAlarm('new', index)}
                          className="hover:bg-gray-100 p-2 rounded text-xs border border-gray-300"
                          title="Edit Alarm"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (window.confirm('You are about to delete this alarm. Are you sure?')) {
                              let updated: AlarmDto[]
                              if (alarm.workOrderAlarmID) {
                                updated = [...newAlarms]
                                updated[index] = { ...alarm, deleted: true }
                              } else {
                                updated = newAlarms.filter((_, i) => i !== index)
                              }
                              updateNewAlarms(updated)
                              // Auto-save with the updated array
                              await handleSaveWorkOrder(undefined, updated)
                            }
                          }}
                          className="hover:bg-red-100 p-2 rounded text-xs border border-red-300 text-red-600"
                          title="Remove"
                        >
                          ❌
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
            </>
          )}
        </div>
      </div>

      {/* Inspection Report */}
      <div className="bg-white rounded-lg shadow mb-3">
        <div className="bg-gray-500 text-white p-4 rounded-t-lg">
          <h5 className="mb-0 text-lg font-medium">Inspection Report</h5>
        </div>
        <div className="p-4 bg-gray-50">
          {/* Inspection Notes */}
          <div className="flex flex-wrap -mx-2 mb-4">
            <div className="w-full px-2">
              <label className="block text-sm font-bold text-gray-700 mb-1">Inspection Notes</label>
              <textarea
                name="inspectionNotes"
                value={formData.inspectionNotes || ''}
                onChange={handleInputChange}
                className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500"
                rows={4}
                placeholder="Enter inspection notes..."
              />
            </div>
          </div>
          
          {/* Inspection Status */}
          <div className="flex flex-wrap -mx-2 mb-4">
            <div className="w-full md:w-1/3 px-2 mb-3">
              <div className="bg-white rounded-lg shadow">
                <div className="p-4">
                                  <h6 className="font-semibold mb-3">Compliance</h6>
                  <div className="flex items-center space-x-4 flex-wrap">
                    {sortComplianceOptions(lookupData?.standardOptions || []).map(option => (
                      <div key={option.id} className="flex items-center">
                        <input
                          type="radio"
                          name="complianceID_FK"
                          value={option.id.toString()}
                          checked={formData.complianceID_FK === option.id}
                          onChange={handleInputChange}
                          className="mr-2"
                          id={`compliance-${option.id}`}
                        />
                        <label className="text-sm" htmlFor={`compliance-${option.id}`}>{option.name}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full md:w-1/3 px-2 mb-3">
              <div className="bg-white rounded-lg shadow">
                <div className="p-4">
                  <h6 className="font-semibold mb-3">Legislation</h6>
                  <div className="flex items-center space-x-4 flex-wrap">
                    {sortComplianceOptions(lookupData?.complianceOptions || []).map(option => (
                      <div key={option.id} className="flex items-center">
                        <input
                          type="radio"
                          name="standardID_FK"
                          value={option.id.toString()}
                          checked={formData.standardID_FK === option.id}
                          onChange={handleInputChange}
                          className="mr-2"
                          id={`standard-${option.id}`}
                        />
                        <label className="text-sm" htmlFor={`standard-${option.id}`}>{option.name}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full md:w-1/3 px-2 mb-3">
              <div className="bg-white rounded-lg shadow">
                <div className="p-4">
                  <h6 className="font-semibold mb-3">Status <span className="text-red-600">*</span></h6>
                  <div className="flex items-center space-x-4 flex-wrap">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="statusID_FK"
                        value="1"
                        checked={formData.statusID_FK === 1}
                        onChange={handleInputChange}
                        className="mr-2"
                        id="status-booked"
                      />
                      <label className="text-sm" htmlFor="status-booked">Booked</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="statusID_FK"
                        value="2"
                        checked={formData.statusID_FK === 2}
                        onChange={handleInputChange}
                        className="mr-2"
                        id="status-completed"
                      />
                      <label className="text-sm" htmlFor="status-completed">Completed</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="statusID_FK"
                        value="4"
                        checked={formData.statusID_FK === 4}
                        onChange={handleInputChange}
                        className="mr-2"
                        id="status-unable"
                      />
                      <label className="text-sm" htmlFor="status-unable">Unable To Complete</label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="statusID_FK"
                        value="3"
                        checked={formData.statusID_FK === 3}
                        onChange={handleInputChange}
                        className="mr-2"
                        id="status-cancelled"
                      />
                      <label className="text-sm" htmlFor="status-cancelled">Cancelled</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quote Required Section */}
          <div className="flex flex-wrap -mx-2 mb-3">
            <div className="w-full px-2">
              <h6 className="text-blue-600 font-semibold mb-3">Quote Required</h6>
            </div>
            <div className="w-full md:w-1/3 px-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Alarms for Upgrade</label>
              <input
                type="text"
                inputMode="numeric"
                name="alarmsRe2022"
                value={formData.alarmsRe2022 || '1'}
                onChange={handleInputChange}
                className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="w-full md:w-1/3 px-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">No. Rooms</label>
              <input
                type="text"
                inputMode="numeric"
                name="noRooms"
                value={formData.noRooms || '0'}
                onChange={handleInputChange}
                className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="w-full md:w-1/3 px-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">No. Levels</label>
              <input
                type="text"
                inputMode="numeric"
                name="noLevels"
                value={formData.noLevels || '0'}
                onChange={handleInputChange}
                className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Work Order Photos */}
      <div className="bg-white rounded-lg shadow mb-3">
        <div className="bg-gray-500 text-white p-4 rounded-t-lg">
          <h5 className="mb-0 text-lg font-medium">Work Order Photos</h5>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap -mx-2">
            <div className="w-full md:w-1/2 px-2 mb-3 md:mb-0">
              <button
                type="button"
                onClick={handleViewWorkOrderPhotos}
                disabled={isLoadingWorkOrderPhotos}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded w-full disabled:opacity-50"
              >
                {isLoadingWorkOrderPhotos ? 'Loading...' : `View Photos${workOrderPhotoCount > 0 ? ` (${workOrderPhotoCount})` : ''}`}
              </button>
            </div>
            <div className="w-full md:w-1/2 px-2">
              <button
                type="button"
                onClick={handleUploadWorkOrderPhoto}
                className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded w-full"
              >
                Upload Photos
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Work Order Invoice and Annual Billing */}
      <div className="flex flex-wrap -mx-2 mb-3">
        <div className="w-full md:w-1/2 px-2">
          <InvoicePayment 
            workOrderId={workOrderId}
            {...(workOrderResponse?.workOrder && {
              invoiceData: {
                invoiceNo: formData.invoiceNo,
                invoiceAmount: formData.invoiceAmount,
                invoiceDate: formData.invoiceDate,
                invoiceStatus: formData.invoiceStatus,
                xeroInvoiceID: formData.xeroInvoiceID
              }
            })}
            onPaymentSuccess={handlePaymentSuccess}
          />
        </div>
        
        <div className="w-full md:w-1/2 px-2">
          <div className="bg-white rounded-lg shadow">
            <div className="bg-gray-500 text-white p-4 rounded-t-lg">
              <h5 className="mb-0 text-lg font-medium">Annual Billing</h5>
            </div>
            <div className="p-4">
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                <input 
                  type="date" 
                  name="annualBillingFrom"
                  value={formatDate(formData.annualBillingFrom)} 
                  readOnly
                  className="border border-gray-300 rounded px-3 py-2 w-full bg-gray-100 text-gray-600 focus:outline-none"
                  placeholder="dd/mm/yyyy"
                />
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input 
                  type="date" 
                  name="annualBillingTo"
                  value={formatDate(formData.annualBillingTo)} 
                  readOnly
                  className="border border-gray-300 rounded px-3 py-2 w-full bg-gray-100 text-gray-600 focus:outline-none"
                  placeholder="dd/mm/yyyy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Creation/Modification Info */}
      <div className="flex flex-wrap -mx-2 mb-3">
        <div className="w-full px-2 text-center">
          <small className="text-gray-500">
            <strong>Date Created:</strong> {workOrder.creationDate ? new Date(workOrder.creationDate).toLocaleDateString() : ''}
            <strong className="ml-3">Last Modified:</strong> {workOrder.modifiedDate ? new Date(workOrder.modifiedDate).toLocaleDateString() : ''}
          </small>
          <br />
          <small className="text-gray-500">
            <strong>Entry Notice:</strong> {workOrder.entryNoticeSentDate ? new Date(workOrder.entryNoticeSentDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
            <strong className="ml-3">SMS Reminder:</strong> {workOrder.smsSentDate ? new Date(workOrder.smsSentDate).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
          </small>
          <br />
          <small className="text-red-600">* Denotes Required Fields</small>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap -mx-2 mb-3">
        <div className="w-full md:w-1/4 px-2 mb-3 md:mb-0">
          <button 
            type="button" 
            className={`font-bold py-2 px-4 rounded w-full ${
              isJSASigned 
                ? 'bg-blue-500 hover:bg-blue-700 text-white' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            onClick={handleSave}
            disabled={!isJSASigned}
          >
            Save
          </button>
        </div>
        <div className="w-full md:w-1/4 px-2 mb-3 md:mb-0">
          <button 
            type="button" 
            className={`font-bold py-2 px-4 rounded w-full ${
              isJSASigned 
                ? 'bg-cyan-500 hover:bg-cyan-700 text-white' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            onClick={handleSaveAndClose}
            disabled={!isJSASigned}
          >
            Save & Close
          </button>
        </div>
        <div className="w-full md:w-1/4 px-2 mb-3 md:mb-0">
          <button 
            type="button" 
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded w-full"
            onClick={handleCloseWorkOrder}
          >
            Close Work Order
          </button>
        </div>
        <div className="w-full md:w-1/4 px-2">
          <button
            type="button"
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded w-full"
            onClick={() => handleOpenSmsModal()}
          >
            Send SMS
          </button>
        </div>
      </div>

      {/* Additional sections from screenshots would go here */}
      
      {/* Hidden file input for image uploads */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="image/*"
        capture={isAndroid ? "environment" : undefined}
        multiple={!isAndroid}
        style={{ display: 'none' }}
      />

      {/* Hidden file input for work order photo uploads */}
      <input
        type="file"
        ref={workOrderFileInputRef}
        onChange={handleWorkOrderFileSelect}
        accept="image/*"
        capture={isAndroid ? "environment" : undefined}
        multiple={!isAndroid}
        style={{ display: 'none' }}
      />

      {/* Photo Modal */}
      {showPhotosModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Alarm Photos</h3>
                <button
                  onClick={() => setShowPhotosModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mt-2">
                {currentAlarmPhotos.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No photos found for this alarm.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {currentAlarmPhotos.map((photo) => (
                      <div key={photo.workOrderAlarmPhotoID} className="border rounded-lg p-2 relative">
                        <img
                          src={apiEndpoints.files.serve(photo.fullPath || photo.workOrderAlarmFile)}
                          alt={`Alarm photo ${photo.workOrderAlarmPhotoID}`}
                          className="w-full h-48 object-cover rounded cursor-pointer"
                          onClick={() => setFullscreenImage(apiEndpoints.files.serve(photo.fullPath || photo.workOrderAlarmFile))}
                        />
                        <button
                          onClick={() => handleDeleteAlarmPhoto(photo.workOrderAlarmPhotoID)}
                          className="absolute top-3 right-3 bg-red-500 hover:bg-red-700 text-white rounded-full p-1.5 shadow-md"
                          title="Delete photo"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowPhotosModal(false)}
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Image Viewer */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 bg-black z-[60] flex items-center justify-center"
          onClick={() => setFullscreenImage(null)}
        >
          <button
            onClick={() => setFullscreenImage(null)}
            className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 z-[61]"
          >
            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={fullscreenImage}
            alt="Full size"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Work Order Photo Modal */}
      {showWorkOrderPhotosModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Work Order Photos</h3>
                <button
                  onClick={() => setShowWorkOrderPhotosModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mt-2">
                {currentWorkOrderPhotos.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No photos found for this work order.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {currentWorkOrderPhotos.map((photo) => (
                      <div key={photo.workOrderPhotoID} className="border rounded-lg p-2 relative">
                        <img
                          src={apiEndpoints.files.serve(photo.fullPath || photo.workOrderFile)}
                          alt={`Work order photo ${photo.workOrderPhotoID}`}
                          className="w-full h-48 object-cover rounded cursor-pointer"
                          onClick={() => setFullscreenImage(apiEndpoints.files.serve(photo.fullPath || photo.workOrderFile))}
                        />
                        <button
                          onClick={() => handleDeleteWorkOrderPhoto(photo.workOrderPhotoID)}
                          className="absolute top-3 right-3 bg-red-500 hover:bg-red-700 text-white rounded-full p-1.5 shadow-md"
                          title="Delete photo"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowWorkOrderPhotosModal(false)}
                  className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SMS Modal */}
      {showSmsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Send SMS</h3>
                <button
                  onClick={handleCloseSmsModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SMS Type</label>
                  <select
                    value={smsType}
                    onChange={(e) => handleSmsTypeChange(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500"
                  >
                    {smsTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SMS Message</label>
                  <textarea
                    value={smsMessage}
                    onChange={(e) => setSmsMessage(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:border-blue-500"
                    rows={5}
                    placeholder="Enter SMS message..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Send to ({selectedSmsContacts.length} selected)
                                  </label>handleSaveWorkOrder
                  <div className="max-h-40 overflow-y-auto border border-gray-300 rounded p-2 space-y-2">
                    {contacts.filter(c => c.tenantPhone && c.tenantPhone.trim() !== '').length > 0 ? (
                      contacts.map((contact, idx) => {
                        if (!contact.tenantPhone || contact.tenantPhone.trim() === '') return null
                        return (
                          <label key={idx} className="flex items-center gap-2 cursor-pointer p-1 hover:bg-gray-50 rounded">
                            <input
                              type="checkbox"
                              checked={selectedSmsContacts.includes(idx)}
                              onChange={() => toggleSmsContactSelection(idx)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="font-medium">{contact.tenantName || 'Unknown'}</span>
                            <span className="text-gray-500">{contact.tenantPhone}</span>
                          </label>
                        )
                      })
                    ) : (
                      <p className="text-gray-500 text-sm py-2">No contacts with phone numbers</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={handleSendSms}
                  disabled={isSendingSms}
                  className={`flex-1 text-white font-bold py-2 px-4 rounded ${
                    isSendingSms
                      ? 'bg-blue-300 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-700'
                  }`}
                >
                  {isSendingSms ? 'Sending...' : 'Send SMS'}
                </button>
                <button
                  onClick={handleCloseSmsModal}
                  disabled={isSendingSms}
                  className="flex-1 bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TechnicianWorkOrder 